import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../api';
import { usePermissions } from '../hooks/usePermissions';
import { logDownload, logPrint } from '../utils/eventLog';
import { printedByLabel } from '../utils/userLabel';
import { parseManagerTitle } from '../utils/parseManagerTitle';
import { loadPeople, peopleFor, findPerson, personLabel } from '../utils/peopleLookup';
import { getKstDateString } from '../utils/dateKst';
import { TaskForm, DetailModal as TaskDetailModal } from './Tasks';
import FileDropZone from '../components/FileDropZone';
import LinkedFormTable from './LinkedFormTable';

const formatDT = (str) => str || '-';

// 영업품목 "옵션" 표시 — 재고관리(제품옵션 추가/삭제)에 등록한 값을 번역하지 않고 그대로 사용.

// '2026. 07. 30. 14:23:11' 또는 '2026-07-30' 등 어떤 형식이든 비교 가능한 타임스탬프로 변환
const parseAnyDate = (str) => {
  if (!str) return 0;
  // 점(.) 형식: '2026. 07. 30. 14:23:11'
  let m = str.match(/(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) return new Date(+m[1], +m[2]-1, +m[3], +m[4], +m[5], +(m[6]||0)).getTime();
  // 대시(-) + 시간 형식: '2026-07-30 14:23:11' 또는 '2026-07-30T14:23:11' (DB 기본 생성일시 포맷)
  m = str.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) return new Date(+m[1], +m[2]-1, +m[3], +m[4], +m[5], +(m[6]||0)).getTime();
  // 대시(-) 날짜만: '2026-07-30'
  m = str.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2]-1, +m[3]).getTime();
  return 0;
};
// 짧은 날짜 표기 'YY.MM.DD'
const toShortDate = (str) => {
  if (!str) return '';
  let m = str.match(/(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})\./) || str.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const pad = n => String(n).padStart(2,'0');
  return `${m[1].slice(2)}.${pad(m[2])}.${pad(m[3])}`;
};

const SORT_OPTIONS = [
  { key:'latest',   label:'최신순' },
  { key:'date',     label:'일자별' },
  { key:'partner',  label:'고객사' },
  { key:'customer', label:'공급처' },
];

// 목록 헤더 <span> — 컬럼명을 클릭할 때마다 오름차순/내림차순이 토글된다(활성 컬럼만 색상 강조).
// 상단 정렬 버튼(SORT_OPTIONS)과 동일한 sortKey/sortDir 상태를 공유해 항상 서로 일치한다.
function SortableTh({ label, sortKey, activeKey, dir, onClick }) {
  const active = activeKey === sortKey;
  return (
    <span onClick={() => onClick(sortKey)}
      title={active ? (dir === 'desc' ? '내림차순 (클릭시 오름차순)' : '오름차순 (클릭시 내림차순)') : '클릭하여 정렬'}
      style={{ cursor:'pointer', userSelect:'none', color: active ? '#185FA5' : '#666', fontWeight: active ? 700 : 600 }}>
      {label}
    </span>
  );
}

export default function SalesActivities() {
  const [list, setList]         = useState([]);
  const [search, setSearch]     = useState('');
  const [sortKey, setSortKey]   = useState('latest'); // 기본: 업데이트 최신순
  const [sortDir, setSortDir]   = useState('desc');    // desc: 내림차순, asc: 오름차순
  const [statusFilter, setStatusFilter] = useState('all'); // all / 진행중 / 완료
  const [pageSize, setPageSize] = useState('all');   // 10/30/50/100/'all' — 기본값: 모두보기
  const [page, setPage]         = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [detail, setDetail]     = useState(null); // 상세 모달 (id)
  const [msg, setMsg]           = useState('');
  const [showQuotationList, setShowQuotationList] = useState(false); // 견적서 목록/작성/수정/조회
  // 영업활동 상세화면의 "📋 견적서 작성" 버튼으로 바로 진입할 때(목록을 거치지 않음) 사용 —
  // 그 활동의 고객사/담당자/영업품목 정보를 견적서 작성폼에 미리 채워준다.
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [quotationPrefill, setQuotationPrefill]   = useState(null);
  // 견적서가 등록/수정/삭제될 때마다 1씩 증가하는 값. 헤더 "📋 견적서" 메뉴(QuotationListModal)와
  // 영업활동 상세(DetailModal)는 서로 다른 모달로 동시에 열려 있을 수 있는데, 한쪽에서 견적서를
  // 지우거나 고쳐도 다른 쪽은 자기가 이미 불러온 목록을 그대로 들고 있어 반영되지 않는 문제가 있었다.
  // 이 값을 DetailModal에 넘겨서 useEffect 의존성으로 쓰면, 값이 바뀔 때마다 다시 불러오게 된다.
  const [quotationsVersion, setQuotationsVersion] = useState(0);
  const bumpQuotationsVersion = () => setQuotationsVersion(v => v + 1);
  // 영업활동 상세의 "🤝 영업지원" 버튼으로 바로 진입하는 업무등록 폼(Tasks.js의 TaskForm 재사용) —
  // 견적서 작성과 동일한 방식으로, 이 활동의 고객사명을 제목 앞부분에 미리 채워준다.
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportContext, setSupportContext]   = useState(null);
  // 영업지원 업무가 등록/수정/삭제될 때마다 1씩 증가 — quotationsVersion과 동일한 용도로,
  // DetailModal이 우측 "영업지원" 목록을 다시 불러오도록 트리거한다.
  const [supportVersion, setSupportVersion] = useState(0);
  const bumpSupportVersion = () => setSupportVersion(v => v + 1);
  // 샘플공급 수량 카드 클릭 시 제품군/제품명/옵션별 내역을 보여주는 모달
  const [showSampleBreakdown, setShowSampleBreakdown] = useState(false);
  // 영업완료 건수 카드 클릭 시 완료건 목록을 보여주는 모달
  const [showCompletedList, setShowCompletedList] = useState(false);
  // 제품군명 표시용(영업품목의 product_group_id → 이름) — 작성폼과 동일하게 /stock/groups 사용
  const [groups, setGroups] = useState([]);
  useEffect(() => { API.get('/stock/groups').then(r => setGroups(r.data || [])).catch(() => {}); }, []);
  // 목록 "영업품목" 칸에는 모델명/옵션까지 다 담긴 product_name 대신 제품군명만 짧게 보여준다.
  // 제품군 연동 없이 직접입력(기타제품)만 등록한 건은 '기타'로 표시(내역 모달과 동일한 규칙).
  const groupNameOf = (a) => a.product_group_id
    ? (groups.find(g => String(g.id) === String(a.product_group_id))?.name || '기타')
    : (a.other_product ? '기타' : '-');

  const user    = JSON.parse(sessionStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'superadmin' || user.role === 'admin';
  const { canWrite: canWriteMenu, hasDetail } = usePermissions();
  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 쓰기 권한을 그대로 따릅니다.
  const canWriteSalesActivities = canWriteMenu('sales-activities');
  const canRegisterActivity = hasDetail('sales-activities', 'register_allowed');
  const canViewDetail = hasDetail('sales-activities', 'detail_view_allowed');
  // 견적서 등록/수정/삭제 — 별도 메뉴를 새로 만들지 않고 'sales-activities'(영업관리) 메뉴의
  // 세부권한을 재사용한다.
  const canQuotationRegister = hasDetail('sales-activities', 'quotation_register_allowed');
  const canQuotationUpdate   = hasDetail('sales-activities', 'quotation_update_allowed');
  const canQuotationDelete   = hasDetail('sales-activities', 'quotation_delete_allowed');

  const load = () => API.get('/sales-activities').then(r => setList(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  // 화면 하단 "최신글/업데이트" 활동피드 바에서 영업관리 항목을 클릭해 들어온 경우, 그 항목의
  // 상세 모달을 바로 연다(DetailModal은 id만 받아 스스로 상세를 불러오므로 목록 로딩을 기다릴
  // 필요가 없다).
  const feedLocation = useLocation();
  const feedNavigate = useNavigate();
  useEffect(() => {
    const ao = feedLocation.state?.activityOpen;
    if (ao) {
      setDetail(ao.itemId);
      feedNavigate(feedLocation.pathname, { replace: true, state: {} });
    }
  }, [feedLocation.state]); // eslint-disable-line

  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 2500); };

  const filtered = list
    .filter(a =>
      (statusFilter === 'all' || (a.status || '진행중') === statusFilter) &&
      (!search ||
        (a.partner_company||'').includes(search) ||
        (a.customer_company||'').includes(search) ||
        (a.partner_manager||'').includes(search) ||
        (a.customer_manager||'').includes(search) ||
        (a.product_name||'').includes(search) ||
        (a.latest_situation||'').includes(search))
    )
    .sort((a, b) => {
      // 완료된 항목은 정렬 방식(최신순/일자별/영업사/고객사)에 관계없이 항상 맨 마지막으로
      const aDone = (a.status === '완료') ? 1 : 0;
      const bDone = (b.status === '완료') ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;

      let result;
      if (sortKey === 'partner')       result = (a.partner_company||'').localeCompare(b.partner_company||'');
      else if (sortKey === 'customer') result = (a.customer_company||'').localeCompare(b.customer_company||'');
      else if (sortKey === 'date')     result = parseAnyDate(a.activity_date) - parseAnyDate(b.activity_date);
      else { // 최신순(기본): 등록일자와 영업사항 업데이트 중 더 최근 시점을 기준으로 최상단에
        const aLatest = a.latest_situation_at || a.created_at || a.activity_date;
        const bLatest = b.latest_situation_at || b.created_at || b.activity_date;
        result = parseAnyDate(aLatest) - parseAnyDate(bLatest);
      }
      return sortDir === 'desc' ? -result : result;
    });

  // 정렬 버튼 클릭: 같은 버튼이면 방향 토글, 다른 버튼이면 새로 선택(기본 내림차순)
  const handleSortClick = (key) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // 검색/필터/정렬/페이지크기가 바뀌면 1페이지로 초기화
  useEffect(() => { setPage(1); }, [search, statusFilter, sortKey, sortDir, pageSize]);

  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageSafe   = Math.min(page, totalPages);
  const paged = pageSize === 'all'
    ? filtered
    : filtered.slice((pageSafe-1)*pageSize, pageSafe*pageSize);

  // 고객사 칸 폭 자동조절 — 입력 글자가 길어 2줄로 넘어가지 않도록, 목록에서 가장 긴 고객사명에
  // 맞춰 칸을 넓힌다(한글 약 15px, 그 외 약 9px, 굵은 글씨 여유 포함). 최소 120px.
  const partnerColW = Math.max(120, Math.ceil(filtered.reduce((mx, a) => {
    const s = String(a.partner_company || '');
    let w = 0;
    for (const ch of s) w += /[ᄀ-ᇿ㄰-㆏가-힯一-鿿]/.test(ch) ? 15 : 9;
    return Math.max(mx, w);
  }, 0) + 20));
  const listCols = `65px 90px ${partnerColW}px 80px 65px 95px 105px 120px 100px 110px 80px 1.3fr`;

  const handleDelete = async (id) => {
    if (!window.confirm('정말 삭제하시겠습니까? (최고관리자만 가능)')) return;
    try {
      await API.delete('/sales-activities/'+id);
      showMsg('삭제 완료!'); setDetail(null); load();
    } catch(err) { showMsg(err.response?.data?.message||'오류'); }
  };

  const thisYM = new Date().toISOString().slice(0,7);
  // 카드 좌측 문구는 "이번달 등록 / 영업 업데이트"로, 우측 숫자도 등록건수와 업데이트건수를
  // 각각 따로 세서 "등록건수 / 업데이트건수" 형태로 보여준다(요청사항: 두 값을 숫자로 분리 표기).
  // 등록건수 = 이번달 신규 등록(created_at 기준), 업데이트건수 = 이번달 영업사항이 업데이트된 건
  // (latest_situation_at 기준) — 같은 건이 둘 다 해당될 수 있어 서로 배타적이지 않다.
  const monthRegisteredCount = list.filter(a => (a.created_at||'').slice(0,7)===thisYM).length;
  const monthUpdatedCount    = list.filter(a => (a.latest_situation_at||'').slice(0,7)===thisYM).length;

  // 영업완료 건수 카드 클릭 시 표시할 완료 상태 활동 목록(최신순)
  const completedList = list
    .filter(a => a.status === '완료')
    .sort((a,b) => {
      const aLatest = a.latest_situation_at || a.created_at || a.activity_date;
      const bLatest = b.latest_situation_at || b.created_at || b.activity_date;
      return parseAnyDate(bLatest) - parseAnyDate(aLatest);
    });

  // 샘플공급 수량 카드 클릭 시 표시할 내역 — 제품군(product_group_id)/제품명(product_name)/
  // 옵션(product_option)의 조합별로 sample_supply를 합산한다. 공급수량이 없는(0/미입력) 건은 제외.
  // 샘플공급 수량 카드 클릭 시 보여줄 내역 — 재고관리에 등록된 제품군/모델명/옵션으로 공급한
  // 건은 그 기준으로 묶고(등록 제품), 목록에 없어 직접입력(기타제품)한 건은 같은 제품명 칸을
  // 공유하지 않도록 별도 구분(isCustom)으로 나눠서 집계한다. 카드 자체의 값은 필터링 없는
  // 전체 list를 그대로 합산하므로(아래 SummaryCard의 value), 이 내역도 동일하게 전체 대상이다.
  const sampleBreakdown = (() => {
    const map = new Map();
    list.forEach(a => {
      const qty = Number(a.sample_supply) || 0;
      if (!qty) return;
      const isCustom = !a.product_group_id && !!(a.other_product && a.other_product.trim());
      const groupName = a.product_group_id
        ? (groups.find(g => String(g.id) === String(a.product_group_id))?.name || '기타')
        : '기타';
      const modelNo = a.product_model_no || '-';
      const optionLabel = a.product_option || '-';
      const customName = a.other_product || a.product_name || '-';
      const key = isCustom
        ? 'custom|' + customName + '|' + optionLabel
        : groupName + '|' + modelNo + '|' + optionLabel;
      if (!map.has(key)) map.set(key, { isCustom, groupName, modelNo, customName, optionLabel, qty: 0 });
      map.get(key).qty += qty;
    });
    return Array.from(map.values())
      .sort((a,b) => (a.isCustom - b.isCustom) || a.groupName.localeCompare(b.groupName) || String(a.modelNo).localeCompare(String(b.modelNo)));
  })();

  // ── 엑셀 내보내기 (첨부받은 Salesactive_Form.xlsx 서식 그대로) ──
  // 서버가 실제 템플릿 파일(backend/templates/Salesactive_Form.xlsx)을 열어 값만 채운 뒤
  // 그 결과물을 그대로 내려주므로, 시트명/열 순서/머리글이 항상 서식과 정확히 일치한다.
  // (backend/routes/sales_activities.js의 GET /export/excel, utils/salesActivityExcel.js 참고)
  const downloadExcel = async () => {
    try {
      const res = await API.get('/sales-activities/export/excel', { responseType: 'blob' });
      const exportFileName = 'IMS_영업관리_'+new Date().toISOString().slice(0,10)+'.xlsx';
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      logDownload('영업관리', exportFileName);
    } catch (err) {
      showMsg('내보내기 중 오류가 발생했습니다.');
    }
  };

  return (
    <div id="sales-activities-print-root" style={{ padding:24, maxWidth:1690 }}>

      {/* 인쇄 설정 */}
      <style>{`
        .print-only { display:none; }

        @media print {
          @page { size: A4 landscape; margin: 6mm; }
          html, body, #root { width:100%; height:auto !important; min-height:0 !important; overflow:visible !important; }
          body * { visibility: hidden; height:auto !important; min-height:0 !important; max-height:none !important; }
          #sales-activities-print-root, #sales-activities-print-root * { visibility: visible; }
          #sales-activities-print-root {
            position: absolute !important; top:0 !important; left:0 !important;
            width:100% !important; max-width:none !important; margin:0 !important; padding:0 !important;
            zoom: 0.6;
          }
          .no-print { display:none !important; }
          .print-only { display:flex !important; }
          body { background:#fff !important; }
        }
      `}</style>

      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ fontSize:20, fontWeight:600, margin:0 }}>🤝 영업관리</h2>
          <p style={{ fontSize:13, color:'#888', margin:'4px 0 0' }}>협력사·고객사 영업활동 내역을 기록하고 관리합니다.</p>
        </div>
        <div className="no-print hdr-actions" style={{ display:'flex', gap:8 }}>
          <button onClick={() => { logPrint('영업관리', '영업관리 목록'); window.print(); }}
            style={{ background:'#475569', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            🖨️ 인쇄
          </button>
          <button onClick={downloadExcel}
            style={{ background:'#16a34a', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            📥 내보내기
          </button>
          <button onClick={() => setShowQuotationList(true)}
            style={{ background:'#0d9488', color:'#fff', border:'none', borderRadius:8,
              padding:'10px 16px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
            📋 견적서
          </button>
          {canRegisterActivity && (
            <button onClick={() => { setShowForm(true); setEditData(null); }}
              style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:8,
                padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
              + 영업활동 등록
            </button>
          )}
        </div>
      </div>

      {msg && <div className="no-print" style={{ background:'#d1fae5', color:'#065f46', padding:'10px 16px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {/* 요약 카드 */}
      <div className="no-print" style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:20 }}>
        <SummaryCard label="전체 활동" value={list.length.toLocaleString()} color="#185FA5" bg="#E6F1FB" />
        <SummaryCard label="이번달 등록 / 영업 업데이트" labelSize={12}
          value={`${monthRegisteredCount.toLocaleString()} / ${monthUpdatedCount.toLocaleString()}`} color="#0F6E56" bg="#E1F5EE" />
        <SummaryCard label="영업완료 건수" value={completedList.length.toLocaleString()} color="#854F0B" bg="#FAEEDA"
          onClick={() => setShowCompletedList(true)} />
        <SummaryCard label="샘플공급 수량" value={list.reduce((sum,a)=>sum+(Number(a.sample_supply)||0),0).toLocaleString()} color="#7C3AED" bg="#EDE9FE"
          onClick={() => setShowSampleBreakdown(true)} />
      </div>

      {/* 검색 + 정렬 */}
      <div className="no-print" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, marginBottom:16 }}>
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="🔍 고객사, 공급처, 담당자, 품목, 영업사항 검색..."
          style={{ width:'50%', padding:'10px 14px', border:'1px solid #ddd', borderRadius:8,
            fontSize:14, boxSizing:'border-box' }} />
        <div style={{ display:'flex', gap:6 }}>
          <button onClick={() => setStatusFilter('all')}
            style={{
              padding:'8px 14px', fontSize:13, fontWeight:600, borderRadius:8, cursor:'pointer',
              border: statusFilter==='all' ? '1px solid #0F6E56' : '1px solid #ddd',
              background: statusFilter==='all' ? '#0F6E56' : '#fff',
              color: statusFilter==='all' ? '#fff' : '#555',
            }}>
            전체보기
          </button>
          {['진행중','완료'].map(st => (
            <button key={st} onClick={() => setStatusFilter(f => f===st ? 'all' : st)}
              style={{
                padding:'8px 14px', fontSize:13, fontWeight:600, borderRadius:8, cursor:'pointer',
                border: statusFilter===st ? '1px solid #0F6E56' : '1px solid #ddd',
                background: statusFilter===st ? '#0F6E56' : '#fff',
                color: statusFilter===st ? '#fff' : '#555',
              }}>
              {st}
            </button>
          ))}
          <span style={{ width:1, background:'#ddd', margin:'0 4px' }} />
          {SORT_OPTIONS.map(opt => (
            <button key={opt.key} onClick={() => handleSortClick(opt.key)}
              style={{
                padding:'8px 14px', fontSize:13, fontWeight:600, borderRadius:8, cursor:'pointer',
                border: sortKey===opt.key ? '1px solid #185FA5' : '1px solid #ddd',
                background: sortKey===opt.key ? '#185FA5' : '#fff',
                color: sortKey===opt.key ? '#fff' : '#555',
              }}>
              {opt.label} {sortKey===opt.key && (sortDir==='desc' ? '▼' : '▲')}
            </button>
          ))}
          <select value={pageSize} onChange={e => setPageSize(e.target.value==='all' ? 'all' : Number(e.target.value))}
            style={{ padding:'8px 10px', fontSize:13, fontWeight:600, borderRadius:8, border:'1px solid #ddd',
              color:'#555', cursor:'pointer', marginLeft:4 }}>
            <option value={10}>10개</option>
            <option value={30}>30개</option>
            <option value={50}>50개</option>
            <option value={100}>100개</option>
            <option value="all">모두보기</option>
          </select>
        </div>
      </div>

      {/* 목록 */}
      <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflowX:'auto' }}>
        <div style={{ minWidth:1420 + (partnerColW - 120) }}>
          <div style={{ display:'grid', gridTemplateColumns:listCols,
            padding:'10px 12px', background:'#f8fafc', fontSize:14, fontWeight:600, color:'#666', borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
            <span>상태</span>
            {/* 목록 컬럼명을 클릭하면 클릭할 때마다 오름차순/내림차순이 토글된다 — 위쪽 정렬
                버튼(최신순/일자별/영업사/고객사)과 같은 sortKey/sortDir 상태를 공유한다. */}
            <SortableTh label="일자" sortKey="date" activeKey={sortKey} dir={sortDir} onClick={handleSortClick} />
            <SortableTh label="고객사" sortKey="partner" activeKey={sortKey} dir={sortDir} onClick={handleSortClick} />
            <span>부서</span><span>직급</span><span>담당자</span><span>연락처</span>
            <SortableTh label="공급처" sortKey="customer" activeKey={sortKey} dir={sortDir} onClick={handleSortClick} />
            <span>영업품목</span><span>샘플공급</span><span>공급수량</span><span style={{textAlign:'left'}}>영업사항(최신)</span>
          </div>

          {paged.length === 0 ? (
            <div style={{ textAlign:'center', padding:'50px 0', color:'#aaa', fontSize:14 }}>등록된 영업활동이 없습니다.</div>
          ) : paged.map(a => (
            <div key={a.id} onClick={() => canViewDetail && setDetail(a.id)}
              style={{ display:'grid', gridTemplateColumns:listCols,
                padding:'11px 12px', fontSize:14, borderBottom:'1px solid #f0f0f0', alignItems:'center', cursor: canViewDetail ? 'pointer' : 'default', textAlign:'center' }}
              onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <span>
                <span style={{ fontSize:11, padding:'3px 9px', borderRadius:12, fontWeight:600,
                  background: a.status==='완료' ? '#E1F5EE' : '#FEF3C7',
                  color:      a.status==='완료' ? '#0F6E56' : '#92400E' }}>
                  {a.status || '진행중'}
                </span>
              </span>
              <span style={{ color:'#666' }}>{a.activity_date || '-'}</span>
              <span style={{ fontWeight:600, whiteSpace:'nowrap' }}>{a.partner_company || '-'}</span>
              <span>{a.partner_department || '-'}</span>
              <span>{a.partner_position || '-'}</span>
              <span>{a.partner_manager || '-'}</span>
              <span style={{ color:'#888' }}>{a.partner_phone || '-'}</span>
              <span style={{ fontWeight:600 }}>{a.customer_company || '-'}</span>
              <span>{groupNameOf(a)}</span>
              <span>{Number(a.sample_supply) > 0 ? Number(a.sample_supply).toLocaleString()+'개 ('+(a.sample_paid_type === '유상' ? '유상' : '무상')+')' : '-'}</span>
              <span>{Number(a.supply_qty) > 0 ? Number(a.supply_qty).toLocaleString()+'개' : '-'}</span>
              <span style={{ color:'#444', textAlign:'left', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {a.latest_situation ? (
                  <>
                    <span style={{ color:'#185FA5', fontWeight:600, marginRight:5 }}>({toShortDate(a.latest_situation_at)})</span>
                    {a.latest_situation}
                  </>
                ) : <span style={{color:'#ccc'}}>-</span>}
                {a.log_count > 1 && <span style={{ color:'#0F6E56', fontSize:11, marginLeft:5 }}>+{a.log_count-1}건 더</span>}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* 페이지네이션 */}
      {pageSize !== 'all' && totalPages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:6, marginTop:16 }}>
          <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={pageSafe===1}
            style={{ width:32, height:32, borderRadius:6, border:'1px solid #ddd', background:'#fff',
              color: pageSafe===1 ? '#ccc' : '#555', cursor: pageSafe===1 ? 'not-allowed' : 'pointer', fontSize:13 }}>
            ◀
          </button>
          {Array.from({length: totalPages}, (_,i)=>i+1)
            .filter(n => n===1 || n===totalPages || Math.abs(n-pageSafe)<=2)
            .reduce((acc, n, i, arr) => {
              if (i>0 && n - arr[i-1] > 1) acc.push('...');
              acc.push(n);
              return acc;
            }, [])
            .map((n, i) => n === '...' ? (
              <span key={'dots'+i} style={{ padding:'0 4px', color:'#aaa' }}>…</span>
            ) : (
              <button key={n} onClick={() => setPage(n)}
                style={{ width:32, height:32, borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600,
                  border: n===pageSafe ? '1px solid #185FA5' : '1px solid #ddd',
                  background: n===pageSafe ? '#185FA5' : '#fff',
                  color: n===pageSafe ? '#fff' : '#555' }}>
                {n}
              </button>
            ))}
          <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={pageSafe===totalPages}
            style={{ width:32, height:32, borderRadius:6, border:'1px solid #ddd', background:'#fff',
              color: pageSafe===totalPages ? '#ccc' : '#555', cursor: pageSafe===totalPages ? 'not-allowed' : 'pointer', fontSize:13 }}>
            ▶
          </button>
          <span style={{ fontSize:12, color:'#999', marginLeft:10 }}>
            전체 {filtered.length.toLocaleString()}건 중 {((pageSafe-1)*pageSize+1).toLocaleString()}–{Math.min(pageSafe*pageSize, filtered.length).toLocaleString()}
          </span>
        </div>
      )}

      {/* 영업완료 건수 카드 클릭 시 완료건 목록 모달 */}
      {showCompletedList && (
        <CompletedListModal
          rows={completedList}
          onClose={() => setShowCompletedList(false)}
          onOpenDetail={canViewDetail ? (id) => { setShowCompletedList(false); setDetail(id); } : null}
        />
      )}

      {/* 샘플공급 수량 카드 클릭 시 제품군/제품명/옵션별 내역 모달 */}
      {showSampleBreakdown && (
        <SampleSupplyBreakdownModal
          rows={sampleBreakdown}
          total={sampleBreakdown.reduce((sum, r) => sum + r.qty, 0)}
          onClose={() => setShowSampleBreakdown(false)}
        />
      )}

      {/* 등록/수정 폼 */}
      {showForm && (
        <SalesActivityForm
          editData={editData}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSaved={() => {
            setShowForm(false); setEditData(null); load();
            showMsg(editData ? '수정 완료!' : '등록 완료!');
          }}
        />
      )}

      {/* 상세(영업사항 이력) 모달 */}
      {detail && (
        <DetailModal
          id={detail}
          isSuperAdmin={isSuperAdmin}
          user={user}
          onClose={() => setDetail(null)}
          onChanged={() => { load(); }}
          onEdit={(a) => { setEditData(a); setShowForm(true); setDetail(null); }}
          onDelete={handleDelete}
          canQuotationRegister={canQuotationRegister}
          canQuotationUpdate={canQuotationUpdate}
          canQuotationDelete={canQuotationDelete}
          quotationsVersion={quotationsVersion}
          onQuotationChanged={bumpQuotationsVersion}
          onQuotationCreate={(a) => {
            setDetail(null);
            setQuotationPrefill({
              customer_company: a.customer_company || '',
              customer_manager: [a.customer_position, a.customer_manager].filter(Boolean).join(' '),
              customer_phone: a.customer_phone || '',
              model_no: a.product_name || '',
              activity_id: a.id || null,
            });
            setShowQuotationForm(true);
          }}
          supportVersion={supportVersion}
          onSupportCreate={(a) => {
            setDetail(null);
            setSupportContext({
              customerCompany: a.customer_company || '',
              partnerCompany: a.partner_company || a.customer_company || '',
              activityId: a.id || null,
            });
            setShowSupportForm(true);
          }}
        />
      )}

      {/* 견적서 목록/작성/수정/조회 (헤더 "📋 견적서" 버튼) */}
      {showQuotationList && (
        <QuotationListModal
          user={user}
          canRegister={canQuotationRegister}
          canUpdate={canQuotationUpdate}
          canDelete={canQuotationDelete}
          onClose={() => setShowQuotationList(false)}
          onChanged={bumpQuotationsVersion}
        />
      )}

      {/* 영업활동 상세의 "📋 견적서 작성" 버튼으로 바로 진입한 견적서 작성폼(목록을 거치지 않음) */}
      {showQuotationForm && (
        <QuotationFormModal
          editData={null}
          prefill={quotationPrefill}
          user={user}
          onClose={() => { setShowQuotationForm(false); setQuotationPrefill(null); }}
          onSaved={() => {
            setShowQuotationForm(false); setQuotationPrefill(null);
            showMsg('견적서가 등록되었습니다.');
            bumpQuotationsVersion();
          }}
        />
      )}

      {/* 영업활동 상세의 "🤝 영업지원" 버튼으로 바로 진입하는 업무등록 폼(Tasks.js의 TaskForm 재사용) */}
      {showSupportForm && (
        <TaskForm
          salesSupportContext={supportContext}
          onClose={() => { setShowSupportForm(false); setSupportContext(null); }}
          onSaved={() => {
            setShowSupportForm(false); setSupportContext(null);
            showMsg('영업지원 업무가 등록되었습니다.');
            bumpSupportVersion();
          }}
        />
      )}

      {/* 인쇄 전용 하단 정보: 좌측 출력일시 / 우측 출력자 */}
      <div className="print-only" style={{ display:'none', justifyContent:'space-between',
        marginTop:14, paddingTop:8, borderTop:'1px solid #ddd', fontSize:12, color:'#555' }}>
        <span>출력일시: {(() => {
          const d = new Date();
          return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
        })()}</span>
        <span>출력자: {printedByLabel(user)}</span>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, color, bg, onClick, labelSize }) {
  return (
    <div onClick={onClick} style={{ background:bg, borderRadius:12, padding:'16px 20px', display:'flex',
      justifyContent:'space-between', alignItems:'center', cursor: onClick ? 'pointer' : 'default',
      boxShadow: onClick ? '0 0 0 1px rgba(0,0,0,0.03)' : 'none', transition:'transform 0.1s, box-shadow 0.1s' }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)'; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.boxShadow = '0 0 0 1px rgba(0,0,0,0.03)'; }}
      title={onClick ? '클릭하여 상세 내역 보기' : undefined}>
      <span style={{ fontSize:labelSize || 14, fontWeight:600, color, lineHeight:1.4 }}>{label}{onClick && <span style={{ fontSize:11, marginLeft:6, opacity:0.7 }}>🔍</span>}</span>
      <span style={{ fontSize:28, fontWeight:700, color }}>{value}</span>
    </div>
  );
}

// ── 영업완료 건수 카드 클릭 시 표시하는 완료건 목록 모달 ──────────────────
function CompletedListModal({ rows, onClose, onOpenDetail }) {
  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:760, maxWidth:'92vw', maxHeight:'80vh',
        display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0, color:'#854F0B' }}>✅ 영업완료 건수 ({rows.length.toLocaleString()}건)</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ overflowY:'auto', flex:1, border:'1px solid #e5e7eb', borderRadius:10 }}>
          <div style={{ display:'grid', gridTemplateColumns:'85px 1fr 1fr 1fr 1.4fr',
            padding:'10px 14px', background:'#f8fafc', fontSize:13, fontWeight:600, color:'#666',
            borderBottom:'1px solid #e5e7eb', textAlign:'center', position:'sticky', top:0 }}>
            <span>일자</span><span>고객사</span><span>공급처</span><span>영업품목</span><span style={{textAlign:'left'}}>영업사항(최신)</span>
          </div>
          {rows.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#aaa', fontSize:14 }}>완료된 영업활동이 없습니다.</div>
          ) : rows.map(a => (
            <div key={a.id} onClick={() => onOpenDetail && onOpenDetail(a.id)}
              style={{ display:'grid', gridTemplateColumns:'85px 1fr 1fr 1fr 1.4fr',
                padding:'10px 14px', fontSize:14, borderBottom:'1px solid #f0f0f0', alignItems:'center',
                textAlign:'center', cursor: onOpenDetail ? 'pointer' : 'default' }}>
              <span style={{ color:'#666' }}>{a.activity_date || '-'}</span>
              <span style={{ fontWeight:600 }}>{a.partner_company || '-'}</span>
              <span style={{ fontWeight:600 }}>{a.customer_company || '-'}</span>
              <span>{a.product_name || '-'}</span>
              <span style={{ color:'#444', textAlign:'left', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {a.latest_situation || <span style={{color:'#ccc'}}>-</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── 샘플공급 수량 카드 클릭 시 표시하는 제품군/모델명/옵션별 내역 모달 ──────
// 재고관리에 등록된 제품(제품군/모델명/옵션)으로 공급한 건과, 목록에 없어 직접입력(기타제품)한
// 건을 같은 표에 섞지 않고 별도 표로 나눠 보여준다(둘 다 sampleBreakdown에서 isCustom으로 구분).
function SampleSupplyBreakdownModal({ rows, total, onClose }) {
  const registered = rows.filter(r => !r.isCustom);
  const custom      = rows.filter(r => r.isCustom);
  const regCols   = '1.1fr 1.2fr 0.8fr 0.9fr';
  const customCols = '2fr 0.8fr 0.9fr';

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:600, maxWidth:'92vw', maxHeight:'82vh',
        display:'flex', flexDirection:'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0, color:'#7C3AED' }}>📦 샘플공급 수량 내역</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ overflowY:'auto', flex:1 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#444', margin:'2px 0 6px' }}>등록 제품 (제품군/모델명/옵션)</div>
          <div style={{ border:'1px solid #e5e7eb', borderRadius:10, marginBottom:18 }}>
            <div style={{ display:'grid', gridTemplateColumns:regCols,
              padding:'10px 14px', background:'#f8fafc', fontSize:13, fontWeight:600, color:'#666',
              borderBottom:'1px solid #e5e7eb', textAlign:'center', position:'sticky', top:0 }}>
              <span>제품군</span><span>모델명</span><span>옵션</span><span>공급수량</span>
            </div>
            {registered.length === 0 ? (
              <div style={{ textAlign:'center', padding:'24px 0', color:'#aaa', fontSize:14 }}>등록 제품으로 공급한 내역이 없습니다.</div>
            ) : registered.map((r, i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:regCols,
                padding:'10px 14px', fontSize:14, borderBottom:'1px solid #f0f0f0', alignItems:'center', textAlign:'center' }}>
                <span style={{ fontWeight:600 }}>{r.groupName}</span>
                <span>{r.modelNo}</span>
                <span>{r.optionLabel}</span>
                <span style={{ fontWeight:700, color:'#7C3AED' }}>{r.qty.toLocaleString()}개</span>
              </div>
            ))}
          </div>

          <div style={{ fontSize:13, fontWeight:600, color:'#444', margin:'2px 0 6px' }}>직접입력 (기타제품)</div>
          <div style={{ border:'1px solid #e5e7eb', borderRadius:10 }}>
            <div style={{ display:'grid', gridTemplateColumns:customCols,
              padding:'10px 14px', background:'#f8fafc', fontSize:13, fontWeight:600, color:'#666',
              borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
              <span>제품명(직접입력)</span><span>옵션</span><span>공급수량</span>
            </div>
            {custom.length === 0 ? (
              <div style={{ textAlign:'center', padding:'24px 0', color:'#aaa', fontSize:14 }}>직접입력으로 공급한 내역이 없습니다.</div>
            ) : custom.map((r, i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:customCols,
                padding:'10px 14px', fontSize:14, borderBottom:'1px solid #f0f0f0', alignItems:'center', textAlign:'center' }}>
                <span style={{ fontWeight:600 }}>{r.customName}</span>
                <span>{r.optionLabel}</span>
                <span style={{ fontWeight:700, color:'#7C3AED' }}>{r.qty.toLocaleString()}개</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', alignItems:'center', marginTop:14,
          paddingTop:14, borderTop:'1px solid #e5e7eb' }}>
          <span style={{ fontSize:14, fontWeight:600, color:'#666', marginRight:10 }}>합계</span>
          <span style={{ fontSize:20, fontWeight:700, color:'#7C3AED' }}>{total.toLocaleString()}개</span>
        </div>
      </div>
    </div>
  );
}

// ── 상세 모달 (영업사항 이력 + 업데이트 입력) ─────────────────
export function DetailModal({ id, isSuperAdmin, user, onClose, onChanged, onEdit, onDelete, title,
  canQuotationRegister, canQuotationUpdate, canQuotationDelete, onQuotationCreate,
  quotationsVersion, onQuotationChanged, supportVersion, onSupportCreate }) {
  const navigate = useNavigate();
  const [data, setData]       = useState(null);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingLogId, setEditingLogId]     = useState(null);
  const [editingLogText, setEditingLogText] = useState('');
  // 이 활동(activity_id)에 연결되어 만들어진 견적서만 골라서 보여준다.
  const [quotations, setQuotations]           = useState([]);
  const [viewQuotationId, setViewQuotationId] = useState(null);
  // 상세 화면에서 바로 "수정" 눌렀을 때(목록 화면을 거치지 않음) 쓰는 작성폼 상태
  const [quotationEditData, setQuotationEditData] = useState(null);
  const [showQuotationEditForm, setShowQuotationEditForm] = useState(false);

  const load = () => API.get('/sales-activities/'+id).then(r => setData(r.data)).catch(()=>{});
  useEffect(() => { load(); }, [id]);

  // 영업품목 표시용 — 저장된 문자열(product_name, "제품군 - 모델명 - 옵션")을 그대로 쓰지 않고,
  // 원본 필드(product_group_id/product_model_no/product_option)로부터 "제품군 - 모델명 (옵션)"
  // 형태로 다시 조합해서 보여준다. 기타제품(직접입력)은 입력한 텍스트를 그대로 사용.
  const [groups, setGroups] = useState([]);
  useEffect(() => { API.get('/stock/groups').then(r => setGroups(r.data || [])).catch(() => {}); }, []);

  // 이 활동에서 "📋 견적서 작성"으로 직접 만든(또는 만들 때 이 활동이 연결된) 견적서만 골라서
  // 보여준다. 예전에는 고객사명이 같은 견적서를 전부 찾아서 보여줬는데, 그러면 같은 고객사로
  // 등록된 다른 영업사의 활동에도 이 견적서가 "동시에" 등록된 것처럼 보이는 문제가 있었다.
  const loadQuotations = () => {
    if (!data) { setQuotations([]); return; }
    API.get('/quotations').then(r => {
      setQuotations((r.data || []).filter(q => String(q.activity_id) === String(data.id)));
    }).catch(() => setQuotations([]));
  };
  // quotationsVersion은 헤더 "📋 견적서" 메뉴(QuotationListModal)에서 이 화면이 열려있는 동안에도
  // 견적서를 등록/수정/삭제하면 값이 바뀐다. 이 상세화면이 이미 열려 있어도 그 변경이 바로
  // 반영되도록, data.id뿐 아니라 quotationsVersion도 의존성에 넣어서 다시 불러온다.
  useEffect(loadQuotations, [data && data.id, quotationsVersion]);
  useEffect(() => { if (quotationsVersion !== undefined) load(); }, [quotationsVersion]);

  // "🤝 영업지원" 버튼으로 이 활동에 연결되어 만들어진 업무(tasks.linked_activity_id)만 골라서
  // 우측 목록에 보여준다. 견적서와 동일한 클라이언트측 필터링 패턴.
  const [supportTasks, setSupportTasks] = useState([]);
  // 우측 "영업지원" 목록에서 항목을 클릭하면, 업무관리(Tasks.js)에서 그 업무를 클릭했을 때와
  // 완전히 동일한 상세 모달(TaskDetailModal)을 그대로 띄운다. 그 모달은 tasks(전체 목록)와
  // userList(회원목록 — 담당자를 "직급 이름"으로 표시하는 데 필요)를 필요로 하므로, 이 항목을
  // 클릭한 시점에 지연 로딩한다.
  const [viewSupportTask, setViewSupportTask] = useState(null);
  const [supportTaskAllList, setSupportTaskAllList] = useState([]);
  const [supportTaskUserList, setSupportTaskUserList] = useState([]);
  const [editSupportTask, setEditSupportTask] = useState(null);
  const [showEditSupportTaskForm, setShowEditSupportTaskForm] = useState(false);
  const openSupportTask = (t) => {
    setViewSupportTask(t);
    API.get('/tasks').then(r => setSupportTaskAllList(r.data || [])).catch(() => setSupportTaskAllList([]));
    API.get('/tasks/users/list').then(r => setSupportTaskUserList(r.data || [])).catch(() => setSupportTaskUserList([]));
  };
  const loadSupportTasks = () => {
    if (!data) { setSupportTasks([]); return; }
    if (!hasDetail('sales-activities', 'support_view_allowed')) { setSupportTasks([]); return; }
    API.get(`/sales-activities/${data.id}/support-tasks`).then(r => {
      setSupportTasks(r.data || []);
    }).catch(() => setSupportTasks([]));
  };
  useEffect(loadSupportTasks, [data && data.id, supportVersion]);
  useEffect(() => { if (supportVersion !== undefined) load(); }, [supportVersion]);

  // 활동 전체 삭제는 서버(list_delete_allowed)와 동일하게 세부권한도 함께 확인
  // (Hook은 반드시 조건/early return 이전, 컴포넌트 최상단에서 호출해야 하므로 여기서 미리 계산)
  // 관리자/생산관리자/협력사 역할 개념이 없어졌으므로, 최고관리자 외에는 예외 없이
  // 계정관리 > 권한설정에서 부여한 값을 그대로 따릅니다.
  const { hasDetail } = usePermissions();
  const canDeleteListPermission = hasDetail('sales-activities', 'list_delete_allowed');
  // "🤝 영업지원" 버튼 — 실제로 업무등록 폼을 그대로 띄워 tasks에 등록하는 기능이므로,
  // 업무관리(tasks) 메뉴의 등록 세부권한을 그대로 재사용해서 노출 여부를 결정한다.
  const canCreateSupportTask = hasDetail('tasks', 'register_allowed');

  if (!data) return null;

  const productGroupName = groups.find(g => String(g.id) === String(data.product_group_id))?.name || '';
  const productNamePart = [productGroupName, data.product_model_no].filter(Boolean).join(' - ');
  const displayProductName = data.other_product
    ? data.other_product
    : productNamePart
      ? productNamePart + (data.product_option ? ` (${data.product_option})` : '')
      : (data.product_option || data.product_name || '');

  // 수정(기본정보/영업사항 추가·수정, 최초 등록된 영업사항 포함): 권한설정 상세설정의 "영업관리 업데이트" 권한
  const canEdit = hasDetail('sales-activities', 'update_allowed');
  const canEditLog = (l) => canEdit;
  // 삭제(활동 전체 또는 개별 영업사항): 최고관리자 또는 등록자 본인만
  const canDelete = isSuperAdmin || data.created_by_username === (user.username || user.name);
  const canDeleteWhole = canDelete && canDeleteListPermission;

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setLoading(true);
    try {
      await API.post('/sales-activities/'+id+'/logs', { content: newNote.trim() });
      setNewNote(''); load(); onChanged();
    } catch(err) { alert(err.response?.data?.message || '오류'); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await API.put('/sales-activities/'+id, { ...data, status: newStatus });
      load(); onChanged();
    } catch(err) { alert(err.response?.data?.message || '오류'); }
  };

  // 첨부파일 다운로드 (로그인 인증 헤더가 실려야 하므로 <a href>가 아닌 axios+blob 방식 사용)
  const handleDownload = async () => {
    try {
      const res = await API.get('/sales-activities/download/'+data.file_name, { responseType:'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.file_ori || data.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      logDownload('영업관리', data.file_ori || data.file_name);
    } catch (err) {
      alert('다운로드 중 오류가 발생했습니다.');
    }
  };

  const startEditLog = (l) => { setEditingLogId(l.id); setEditingLogText(l.content); };
  const cancelEditLog = () => { setEditingLogId(null); setEditingLogText(''); };
  const saveEditLog = async (logId) => {
    if (!editingLogText.trim()) return;
    try {
      await API.put(`/sales-activities/${id}/logs/${logId}`, { content: editingLogText.trim() });
      cancelEditLog(); load(); onChanged();
    } catch(err) { alert(err.response?.data?.message || '오류'); }
  };
  const deleteLog = async (logId) => {
    if (!window.confirm('이 영업사항 이력을 삭제하시겠습니까? (최고관리자만 가능)')) return;
    try {
      await API.delete(`/sales-activities/${id}/logs/${logId}`);
      load(); onChanged();
    } catch(err) { alert(err.response?.data?.message || '오류'); }
  };

  const rowStyle = { display:'flex', gap:10, fontSize:14, marginBottom:8 };
  const labelStyle = { width:70, color:'#555', flexShrink:0, fontWeight:500 };
  const grid2 = { display:'grid', gridTemplateColumns:'1fr 1fr', columnGap:12, fontSize:14, marginBottom:8, alignItems:'center' };
  const grid4 = { display:'grid', gridTemplateColumns:'repeat(4, 1fr)', columnGap:12, fontSize:14, marginBottom:8, alignItems:'center' };
  const Fld = ({ k, v, extra }) => (
    <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
      <span style={{ color:'#555', fontWeight:500, flexShrink:0 }}>{k}</span>
      <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v || '-'}</span>
      {extra}
    </div>
  );
  // 영업사/고객사 상세를 표처럼 칸 나눠 정렬하기 위한 공통 컬럼: 회사명 | 부서 | 직급 | 담당자 | 연락처
  const colWidths = [120, 70, 65, 90, 150];
  const cellStyle = (i) => ({
    width: colWidths[i], flexShrink:0, padding:'0 10px',
    borderRight: i < colWidths.length-1 ? '1px solid transparent' : 'none',
    color: i===0 ? '#1a1a1a' : '#444', fontWeight: i===0 ? 600 : 400,
    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'
  });

  // 상세 내용 전체(스크롤에 안 보이는 이력 포함)를 별도 창으로 인쇄
  const handlePrint = () => {
    const companySubTable = (company, department, position, manager, phone) => `
      <table style="border-collapse:collapse;width:100%;">
        <tr style="background:#f8fafc;font-size:11px;color:#888;">
          <td style="border:none;padding:4px 8px;">회사명</td>
          <td style="border:none;padding:4px 8px;">부서</td>
          <td style="border:none;padding:4px 8px;">직급</td>
          <td style="border:none;padding:4px 8px;">담당자</td>
          <td style="border:none;padding:4px 8px;">연락처</td>
        </tr>
        <tr style="font-size:13px;">
          <td style="border:none;padding:5px 8px;font-weight:600;">${company||'-'}</td>
          <td style="border:none;padding:5px 8px;">${department||''}</td>
          <td style="border:none;padding:5px 8px;">${position||''}</td>
          <td style="border:none;padding:5px 8px;">${manager||''}</td>
          <td style="border:none;padding:5px 8px;">${phone||''}</td>
        </tr>
      </table>`;

    const infoRows = [
      ['일자', data.activity_date, false],
      ['상태', data.status || '진행중', false],
      ['고객사', companySubTable(data.partner_company, data.partner_department, data.partner_position, data.partner_manager, data.partner_phone), true],
      ['공급처', data.customer_company ? `<span style="padding:0 8px;font-weight:600;">${data.customer_company}</span>` : '', true],
      ['영업품목', displayProductName, false],
      ['샘플공급', data.sample_supply, false],
      ['공급수량', `${Number(data.supply_qty || 0).toLocaleString()}개 (출고관리 출고완료 기준)`, false],
      ['등록자', [[data.created_by_position, data.created_by].filter(Boolean).join(' '), data.created_at?.slice(0,16)].filter(Boolean).join(' | '), false],
      ['첨부파일', data.file_ori || '', false],
    ].filter(([,v]) => v);

    const infoHtml = infoRows.map(([label, val, isTable]) =>
      `<tr><td style="width:90px;color:#888;padding:6px 10px;border-bottom:1px solid #eee;vertical-align:top;">${label}</td>
        <td style="padding:${isTable?'6px 4px':'6px 10px'};border-bottom:1px solid #eee;${isTable?'':'font-weight:600;'}">${val}</td></tr>`
    ).join('');

    const logsHtml = (!data.logs || data.logs.length === 0)
      ? `<div style="color:#999;padding:16px 0;">등록된 영업사항이 없습니다.</div>`
      : data.logs.slice().reverse().map(l =>
          `<div style="padding:10px 12px;background:#fafafa;border-radius:8px;margin-bottom:8px;">
             <div style="font-size:13px;white-space:pre-wrap;line-height:1.6;">${(l.content||'').replace(/</g,'&lt;')}</div>
             <div style="font-size:11px;color:#aaa;margin-top:4px;">${l.created_by||''} · ${l.created_at||''}</div>
           </div>`
        ).join('');

    const now = new Date();
    const printedAt = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const printedBy = printedByLabel(user);
    const pw = window.open('', '_blank', 'width=900,height=1000');
    pw.document.write(`<!DOCTYPE html><html><head><title>영업활동 상세</title>
      <style>
        @page { size: A4; margin: 0; }
        body { font-family: sans-serif; color:#222; padding:15mm; box-sizing:border-box; }
        h2 { font-size:20px; margin-bottom:16px; }
        table { border-collapse: collapse; width:100%; margin-bottom:20px; }
        h3 { font-size:15px; margin:20px 0 10px; }
        .footer { display:flex; justify-content:space-between; margin-top:14px; padding-top:8px;
          border-top:1px solid #ddd; font-size:11px; color:#555; }
      </style></head><body>
      <h2>🤝 영업활동 상세</h2>
      <table>${infoHtml}</table>
      <h3>📝 영업사항 업데이트 이력</h3>
      ${logsHtml}
      <div class="footer"><span>출력일시: ${printedAt}</span><span>출력자: ${printedBy}</span></div>
      </body></html>`);
    pw.document.close(); pw.focus();
    logPrint('영업관리', data.partner_company || data.customer_company || '영업활동 상세');
    setTimeout(() => { pw.print(); pw.close(); }, 400);
  };

  return (
    <>
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:720, maxWidth:'94vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>{title || '영업활동 상세'}</h3>
            {/* 상태 — 제목 바로 오른쪽에 "진행중"처럼 표기 (수정권한이 있으면 선택 가능) */}
            {canEdit ? (
              <select value={data.status || '진행중'} onChange={e => handleStatusChange(e.target.value)}
                style={{ fontSize:13, padding:'4px 10px', border:'1px solid #ddd', borderRadius:8, cursor:'pointer',
                  fontWeight:600,
                  background: data.status==='완료' ? '#E1F5EE' : '#FEF3C7',
                  color:      data.status==='완료' ? '#0F6E56' : '#92400E' }}>
                <option value="진행중">진행중</option>
                <option value="완료">완료</option>
              </select>
            ) : (
              <span style={{ fontSize:12, padding:'3px 9px', borderRadius:12, fontWeight:600,
                background: data.status==='완료' ? '#E1F5EE' : '#FEF3C7',
                color:      data.status==='완료' ? '#0F6E56' : '#92400E' }}>
                {data.status || '진행중'}
              </span>
            )}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {canCreateSupportTask && onSupportCreate && (
              <button className="hdr-btn" onClick={() => onSupportCreate(data)}>
                🤝 영업지원
              </button>
            )}
            {canQuotationRegister && onQuotationCreate && (
              <button className="hdr-btn" onClick={() => onQuotationCreate(data)}>
                📋 견적서 작성
              </button>
            )}
            <button className="hdr-btn" onClick={handlePrint}>
              🖨️ 인쇄
            </button>
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        <div style={{ background:'#f8fafc', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
          {/* 모든 항목은 "일자"와 동일한 글자 스타일(라벨 #555/500, 값 기본) — 한 줄에 2개는 2분할, 4개는 4분할(좌측정렬) */}
          <div style={grid2}>
            <Fld k="일자" v={data.activity_date} />
            <Fld k="영업품목" v={displayProductName} />
          </div>
          <div style={grid2}>
            <Fld k="고객사" v={data.partner_company} />
            <Fld k="공급처" v={data.customer_company} />
          </div>
          <div style={grid4}>
            <Fld k="부서" v={data.partner_department} />
            <Fld k="직급" v={data.partner_position} />
            <Fld k="담당자" v={data.partner_manager} />
            <Fld k="연락처" v={data.partner_phone} />
          </div>
          <div style={grid2}>
            <Fld k="샘플공급" v={data.sample_supply ? Number(data.sample_supply).toLocaleString()+'개' : '-'}
              extra={data.sample_supply > 0 && (
                <span style={{ fontSize:12, padding:'2px 8px', borderRadius:10, fontWeight:600,
                  background: data.sample_paid_type==='유상' ? '#EDE9FE' : '#F1F5F9',
                  color:      data.sample_paid_type==='유상' ? '#7C3AED' : '#64748B' }}>
                  {data.sample_paid_type === '유상' ? '유상' : '무상'}
                </span>
              )} />
            <Fld k="공급수량" v={Number(data.supply_qty || 0).toLocaleString()+'개'} />
          </div>
          {data.linked_shipment_id && (
            <div style={{ marginBottom:8 }}>
              <button onClick={() => navigate(`/sales?viewShipment=${data.linked_shipment_id}`)}
                style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:6,
                  padding:'4px 10px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                🚚 출고 상세보기·수정
              </button>
            </div>
          )}
          <div style={rowStyle}>
            <span style={labelStyle}>등록자</span>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flex:1, minWidth:0 }}>
              <span>{[data.created_by_position, data.created_by].filter(Boolean).join(' ') || '-'}</span>
              <span>{data.created_at ? data.created_at.slice(0,16) : '-'}</span>
            </div>
          </div>

          {data.file_name && (
            <div style={rowStyle}>
              <span style={labelStyle}>첨부파일</span>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ color:'#185FA5' }}>📎 {data.file_ori || data.file_name}</span>
                <button onClick={handleDownload}
                  style={{ background:'#185FA5', color:'#fff', border:'none', borderRadius:6,
                    padding:'5px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  다운로드
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          <div>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:8 }}>📋 견적서</div>
            {quotations.length === 0 ? (
              <div style={{ color:'#aaa', fontSize:13, textAlign:'center', padding:'10px 0' }}>작성된 견적서가 없습니다.</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {quotations.map(q => (
                  <div key={q.id} onClick={() => setViewQuotationId(q.id)}
                    style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 10px',
                      background:'#f0f6ff', border:'1px solid #dbeafe', borderRadius:8, cursor:'pointer', fontSize:12 }}
                    onMouseEnter={e => e.currentTarget.style.background='#e0edff'}
                    onMouseLeave={e => e.currentTarget.style.background='#f0f6ff'}>
                    <span>
                      <span style={{ color:'#666' }}>{q.quote_date || '-'}</span>
                      {' · '}
                      <span style={{ fontWeight:600, color:'#185FA5' }}>{q.top_model_no || '견적서'}</span>
                    </span>
                    <span style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ fontWeight:600 }}>{Number(q.total_amount||0).toLocaleString()}원</span>
                      <span style={{ color:'#888' }}>{q.created_by || ''}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {hasDetail('sales-activities','support_view_allowed') && (
            <div>
              <div style={{ fontSize:14, fontWeight:600, marginBottom:8 }}>🤝 영업지원</div>
              {supportTasks.length === 0 ? (
                <div style={{ color:'#aaa', fontSize:13, textAlign:'center', padding:'10px 0' }}>등록된 영업지원이 없습니다.</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {supportTasks.map(t => (
                    <div key={t.id} onClick={() => openSupportTask(t)}
                      style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 10px',
                        background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:8, cursor:'pointer', fontSize:12 }}
                      onMouseEnter={e => e.currentTarget.style.background='#dbeafe'}
                      onMouseLeave={e => e.currentTarget.style.background='#eff6ff'}>
                      <span style={{ fontWeight:600, color:'#2563eb' }}>{t.title || '영업지원'}</span>
                      <span style={{ color:'#888' }}>{t.manager || ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ fontSize:14, fontWeight:600, marginBottom:8 }}>📝 영업사항 업데이트 이력</div>
        <div style={{ maxHeight:220, overflowY:'auto', marginBottom:14 }}>
          {(!data.logs || data.logs.length===0) ? (
            <div style={{ color:'#aaa', fontSize:13, textAlign:'center', padding:'16px 0' }}>등록된 영업사항이 없습니다.</div>
          ) : data.logs.slice().reverse().map(l => (
            <div key={l.id} style={{ padding:'10px 12px', background:'#fafafa', borderRadius:8, marginBottom:6 }}>
              {editingLogId === l.id ? (
                <div>
                  <textarea value={editingLogText} onChange={e => setEditingLogText(e.target.value)}
                    style={{ width:'100%', minHeight:60, padding:'8px 10px', border:'1px solid #185FA5', borderRadius:6,
                      fontSize:13, boxSizing:'border-box', resize:'vertical', marginBottom:6 }} />
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => saveEditLog(l.id)}
                      style={{ flex:1, padding:'6px', background:'#185FA5', color:'#fff', border:'none', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer' }}>
                      저장
                    </button>
                    <button onClick={cancelEditLog}
                      style={{ flex:1, padding:'6px', background:'#fff', color:'#888', border:'1px solid #ddd', borderRadius:6, fontSize:12, cursor:'pointer' }}>
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, color:'#333', whiteSpace:'pre-wrap', lineHeight:1.5 }}>{l.content}</div>
                    <div style={{ fontSize:11, color:'#aaa', marginTop:4 }}>{l.created_by} · {formatDT(l.created_at)}</div>
                  </div>
                  {(canEditLog(l) || canDelete) && (
                    <div style={{ display:'flex', gap:4, flexShrink:0 }}>
                      {canEditLog(l) && (
                        <button onClick={() => startEditLog(l)}
                          style={{ background:'none', border:'none', cursor:'pointer', fontSize:13 }} title="수정">✏️</button>
                      )}
                      {canDelete && (
                        <button onClick={() => deleteLog(l.id)}
                          style={{ background:'none', border:'none', cursor:'pointer', fontSize:13 }} title="삭제">🗑️</button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {canEdit && (
          <div style={{ marginBottom: 16 }}>
            <textarea value={newNote} onChange={e => setNewNote(e.target.value)}
              placeholder="새로운 영업사항을 입력하세요..."
              style={{ width:'100%', minHeight:60, padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                fontSize:13, boxSizing:'border-box', resize:'vertical', marginBottom:8 }} />
            <button onClick={handleAddNote} disabled={loading || !newNote.trim()}
              style={{ width:'100%', padding:'10px', background: loading||!newNote.trim() ? '#aaa' : '#185FA5',
                color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:600,
                cursor: loading||!newNote.trim() ? 'not-allowed' : 'pointer' }}>
              {loading ? '저장 중...' : '+ 영업사항 업데이트 추가'}
            </button>
          </div>
        )}

        <div style={{ display:'flex', gap:8 }}>
          {canEdit && (
            <button onClick={() => onEdit(data)}
              style={{ flex:1, padding:'10px', background:'#f0f6ff', color:'#185FA5', border:'1px solid #185FA5',
                borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
              ✏️ 기본정보 수정
            </button>
          )}
          {canDeleteWhole && (
            <button onClick={() => onDelete(data.id)}
              style={{ flex:1, padding:'10px', background:'#fee2e2', color:'#dc2626', border:'1px solid #dc2626',
                borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:500 }}>
              🗑 삭제
            </button>
          )}
        </div>
        {!canEdit && !canDeleteWhole && (
          <p style={{ fontSize:12, color:'#aaa', textAlign:'center', marginTop:10 }}>
            수정은 관리자 또는 권한설정에서 쓰기 권한을 받은 계정만, 삭제는 최고관리자 또는 등록자 본인만 가능합니다.
          </p>
        )}
      </div>
    </div>
    {viewQuotationId && (
      <QuotationViewModal id={viewQuotationId} canUpdate={canQuotationUpdate} canDelete={canQuotationDelete}
        onClose={() => setViewQuotationId(null)}
        onEdit={(q) => { setViewQuotationId(null); setQuotationEditData(q); setShowQuotationEditForm(true); }}
        onDeleted={() => {
          setViewQuotationId(null); loadQuotations();
          // 삭제할 때도 수정과 마찬가지로 "영업사항 업데이트 이력"에 삭제 사실이 한 줄 남으므로
          // (backend logToActivity), 상세 화면을 함께 새로고침한다. 예전에는 이 줄이 없어서 목록을
          // 다시 열기 전까지는 삭제 이력이 안 보였다.
          load(); onChanged();
          if (onQuotationChanged) onQuotationChanged();
        }} />
    )}
    {showQuotationEditForm && (
      <QuotationFormModal editData={quotationEditData} user={user}
        onClose={() => { setShowQuotationEditForm(false); setQuotationEditData(null); }}
        onSaved={() => {
          setShowQuotationEditForm(false); setQuotationEditData(null);
          loadQuotations();
          // 견적서를 수정하면 이 활동의 "영업사항 업데이트 이력"에도 자동으로 한 줄 남으므로
          // (backend logToActivity), 상세 화면도 함께 새로고침해서 그 이력이 바로 보이게 한다.
          load(); onChanged();
          if (onQuotationChanged) onQuotationChanged();
        }} />
    )}
    {viewSupportTask && (
      <TaskDetailModal
        task={viewSupportTask}
        tasks={supportTaskAllList}
        userList={supportTaskUserList}
        onClose={() => setViewSupportTask(null)}
        onEdit={(t) => { setViewSupportTask(null); setEditSupportTask(t); setShowEditSupportTaskForm(true); }}
        onDelete={async (id) => {
          if (!window.confirm('정말 삭제하시겠습니까?')) return;
          await API.delete('/tasks/'+id);
          setViewSupportTask(null); loadSupportTasks();
        }}
        onStatusChange={(updated) => { setViewSupportTask(updated); loadSupportTasks(); }}
        onTasksChanged={() => {
          API.get('/tasks').then(r => setSupportTaskAllList(r.data || [])).catch(() => {});
          loadSupportTasks();
        }}
      />
    )}
    {showEditSupportTaskForm && (
      <TaskForm
        editData={editSupportTask}
        onClose={() => { setShowEditSupportTaskForm(false); setEditSupportTask(null); }}
        onSaved={() => {
          setShowEditSupportTaskForm(false); setEditSupportTask(null);
          loadSupportTasks();
        }}
      />
    )}
    </>
  );
}

// ── 등록/수정 폼 ─────────────────────────────
function SalesActivityForm({ editData, onClose, onSaved }) {
  // 수정 화면에서도 최초 등록된 영업사항(logs[0])을 함께 보여주고 고칠 수 있도록,
  // DetailModal에서 넘겨준 editData.logs(작성일시 순 ASC)의 첫 번째 항목을 최초 영업사항으로 사용한다.
  // (editData가 목록 화면에서 바로 넘어온 경우 등 logs가 없을 수도 있으므로 optional chaining으로 방어)
  const initialLog = editData?.logs?.[0] || null;
  const [form, setForm] = useState({
    activity_date:      editData?.activity_date      || getKstDateString(),
    partner_company:    editData?.partner_company     || '',
    partner_department: editData?.partner_department   || '',
    partner_position:   editData?.partner_position     || '',
    partner_manager:    editData?.partner_manager     || '',
    partner_phone:      editData?.partner_phone       || '',
    customer_company:   editData?.customer_company    || '',
    customer_department:editData?.customer_department || '',
    customer_position:  editData?.customer_position   || '',
    customer_manager:   editData?.customer_manager    || '',
    customer_phone:     editData?.customer_phone      || '',
    // 영업품목 — 재고관리 제품군/옵션에서 선택(product_group_id/product_option), 목록에 없는
    // 품목은 기타제품에 직접 입력(other_product). product_name은 위 선택 내용을 사람이 읽을 수
    // 있는 문자열로 계산해서 담아두는 값(목록/인쇄/엑셀 등 기존 화면 호환용) — 제출 직전에
    // buildProductName()으로 자동 계산되므로 여기서는 초기값만 잡아둔다.
    product_group_id:  editData?.product_group_id || '',
    product_model_no:  editData?.product_model_no  || '',
    product_option:    editData?.product_option    || '',
    // 예전 데이터(제품군 연동 이전에 등록된 자유 텍스트 product_name)를 수정하러 들어오면,
    // 어느 제품군인지 알 수 없으므로 기타제품 칸에 그대로 옮겨서 값을 잃지 않게 한다.
    other_product:     editData?.other_product || (!editData?.product_group_id ? (editData?.product_name || '') : ''),
    product_name:       editData?.product_name        || '',
    sample_supply:      editData?.sample_supply        || '',
    sample_paid_type:   editData?.sample_paid_type     || '무상', // 유상/무상 체크박스
    status:              editData?.status               || '진행중',
    situation:          initialLog?.content || '', // 신규 등록시엔 최초 영업사항, 수정시엔 기존 최초 영업사항 내용으로 초기화
  });
  const [file, setFile]       = useState(null);
  const [loading, setLoading] = useState(false);

  // 영업품목 — 재고관리(Inventory)의 제품군/모델명/옵션을 그대로 가져와 쓴다(출고등록 화면과
  // 동일한 /stock/groups, /stock/products, /stock/columns?group_id=&category=완제품 조합).
  const [groups, setGroups] = useState([]);
  useEffect(() => { API.get('/stock/groups').then(r => setGroups(r.data || [])).catch(() => {}); }, []);
  const [products, setProducts] = useState([]);
  useEffect(() => { API.get('/stock/products').then(r => setProducts(r.data || [])).catch(() => {}); }, []);
  // 선택한 제품군에 등록된 모델명만 골라 보여준다(출고관리 등록폼과 동일한 필터 방식).
  const filteredProducts = (products || []).filter(p => !form.product_group_id || String(p.group_id) === String(form.product_group_id));
  const [groupOptions, setGroupOptions]     = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  useEffect(() => {
    if (!form.product_group_id) { setGroupOptions([]); return; }
    setLoadingOptions(true);
    API.get('/stock/columns', { params: { group_id: form.product_group_id, category: '완제품' } })
      .then(r => setGroupOptions((r.data?.items || []).map(i => i.name)))
      .catch(() => setGroupOptions([]))
      .finally(() => setLoadingOptions(false));
  }, [form.product_group_id]);

  // 업체관리에 등록된 업체(고객사·협력사) — 영업사/고객사명을 선택하거나 정확히 입력하면
  // 담당자·직급·연락처를 업체관리 등록 정보로 바로 채운다(전용 조회 라우트: 메뉴 권한 불필요).
  const [companyList, setCompanyList] = useState([]);
  // 명함관리 + 업체관리 담당자 (담당자 자동완성용)
  const [people, setPeople] = useState([]);
  useEffect(() => { loadPeople().then(setPeople); }, []);
  useEffect(() => {
    Promise.all([
      API.get('/companies/customers-lookup', { params: { category: '협력사' } }).catch(() => ({ data: [] })),
      API.get('/companies/customers-lookup', { params: { category: '고객사' } }).catch(() => ({ data: [] })),
    ]).then(([a, b]) => setCompanyList([...(a.data || []), ...(b.data || [])]));
  }, []);
  // (주)/주식회사/공백 차이는 무시하고 비교
  const normCo = (s) => (s || '').toLowerCase().replace(/\(주\)|㈜|주식회사|\(유\)|유한회사|\s+/g, '');
  const findCompany = (name) => {
    const t = (name || '').trim();
    if (!t) return null;
    const exact = companyList.find(c => c.company_name === t);
    if (exact) return exact;
    const n = normCo(t);
    if (!n) return null;
    const hits = companyList.filter(c => normCo(c.company_name) === n);
    return hits.length === 1 ? hits[0] : null;
  };
  const findCompanyLoose = (name) => {
    const t = normCo(name);
    if (!t) return null;
    const hits = companyList.filter(c => normCo(c.company_name).includes(t));
    return hits.length === 1 ? hits[0] : null;
  };
  // kind: 'partner' | 'customer'
  const applyCompany = (kind, c, base) => {
    const { name: mgrName, position: mgrPosition } = parseManagerTitle(c.manager_name || '');
    const patch = kind === 'partner'
      ? { partner_company: c.company_name, partner_department: '', partner_position: mgrPosition || '', partner_manager: mgrName || '', partner_phone: c.manager_contact || '' }
      : { customer_company: c.company_name, customer_position: mgrPosition || '', customer_manager: mgrName || '', customer_phone: c.manager_contact || '' };
    // 수정 모드: 아래쪽 부서/직급/담당자/연락처가 이미 입력돼 있으면 그대로 유지하고,
    // 비어 있는 칸만 업체관리 등록값으로 채운다.
    if (editData) {
      Object.keys(patch).forEach(k => {
        if (k !== 'partner_company' && k !== 'customer_company' && String(base[k] || '').trim()) patch[k] = base[k];
      });
    }
    return { ...base, ...patch };
  };
  const handleCompanyChange = (kind) => (e) => {
    const v = e.target.value;
    const key = kind === 'partner' ? 'partner_company' : 'customer_company';
    const hit = findCompany(v);
    setForm(prev => hit ? applyCompany(kind, hit, { ...prev, [key]: v }) : { ...prev, [key]: v });
  };
  const handleCompanyBlur = (kind) => () => {
    const key = kind === 'partner' ? 'partner_company' : 'customer_company';
    if (findCompany(form[key])) return;
    const hit = findCompanyLoose(form[key]);
    if (hit) setForm(prev => applyCompany(kind, hit, prev));
  };

  const handleChange = e => {
    const { name, value } = e.target;
    if (name === 'partner_manager') {
      // 명함관리/업체관리에 등록된 이름이면 직급·연락처·부서를 함께 자동입력
      const person = findPerson(people, value, form.partner_company);
      if (person) {
        setForm({ ...form, partner_manager: value,
          partner_position: person.position || form.partner_position,
          partner_phone: person.phone || form.partner_phone,
          partner_department: person.department || form.partner_department });
        return;
      }
    }
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async () => {
    if (!form.partner_company && !form.customer_company) {
      return alert('고객사 또는 공급처 중 하나는 입력해주세요.');
    }
    setLoading(true);
    try {
      // 영업품목 표시용 문자열(product_name) 계산 — 기타제품을 입력했으면 그 텍스트를 그대로,
      // 아니면 선택한 제품군명 + 모델명 + 옵션을 합쳐서 만든다(모두 비어있으면 빈 문자열).
      const groupName = groups.find(g => String(g.id) === String(form.product_group_id))?.name || '';
      const optionLabel = form.product_option || '';
      const computedProductName = form.other_product.trim()
        ? form.other_product.trim()
        : [groupName, form.product_model_no, optionLabel].filter(Boolean).join(' - ');
      const submitForm = { ...form, product_name: computedProductName };

      const fd = new FormData();
      Object.entries(submitForm).forEach(([key, val]) => fd.append(key, val ?? ''));
      if (file) fd.append('file', file);

      if (editData) {
        await API.put('/sales-activities/'+editData.id, fd, { headers:{ 'Content-Type':'multipart/form-data' } });
        // 기본정보 수정 API(PUT /:id)는 situation을 반영하지 않으므로, 최초 영업사항은
        // 별도로 로그 수정/등록 API를 통해 반영한다.
        const trimmed = (form.situation || '').trim();
        if (initialLog) {
          if (trimmed && trimmed !== (initialLog.content || '').trim()) {
            await API.put(`/sales-activities/${editData.id}/logs/${initialLog.id}`, { content: trimmed });
          }
        } else if (trimmed) {
          // 등록 당시 최초 영업사항을 비워뒀던 경우, 수정 화면에서 새로 입력하면 첫 로그로 추가한다.
          await API.post(`/sales-activities/${editData.id}/logs`, { content: trimmed });
        }
      } else {
        await API.post('/sales-activities', fd, { headers:{ 'Content-Type':'multipart/form-data' } });
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const lbl = { display:'block', fontSize:13, fontWeight:600, marginBottom:6, marginTop:14, color:'#333' };
  const inp = { width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:14, boxSizing:'border-box' };
  const sectionTitle = { fontSize:13, fontWeight:700, color:'#185FA5', marginTop:18, marginBottom:4 };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:28, width:520, maxWidth:'92vw', maxHeight:'88vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>{editData ? '영업활동 수정' : '영업활동 등록'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <div style={{ flex:1 }}>
            <label style={lbl}>일자</label>
            <input type="date" name="activity_date" value={form.activity_date} onChange={handleChange} style={inp} />
          </div>
          <div style={{ width:120 }}>
            <label style={lbl}>상태</label>
            <select name="status" value={form.status} onChange={handleChange} style={inp}>
              <option value="진행중">진행중</option>
              <option value="완료">완료</option>
            </select>
          </div>
        </div>

        <div style={sectionTitle}>🤝 고객사</div>
        <input name="partner_company" list="sa-company-list" value={form.partner_company} onChange={handleCompanyChange('partner')} onBlur={handleCompanyBlur('partner')} style={{...inp, marginBottom:8}} placeholder="고객사명 (업체관리 등록 업체는 선택 시 자동입력)" />
        <datalist id="sa-company-list">{companyList.map(c => <option key={c.id} value={c.company_name} />)}</datalist>
        <div style={{ display:'flex', gap:8 }}>
          <input name="partner_department" value={form.partner_department} onChange={handleChange} style={inp} placeholder="부서" />
          <input name="partner_position" value={form.partner_position} onChange={handleChange} style={{...inp, width:90}} placeholder="직급" />
          <input name="partner_manager" value={form.partner_manager} onChange={handleChange} style={inp} list="sa-people-list" placeholder="담당자" />
          <datalist id="sa-people-list">{peopleFor(people, form.partner_company).map((p, i) => <option key={i} value={p.name} label={personLabel(p)} />)}</datalist>
          <input name="partner_phone" value={form.partner_phone} onChange={handleChange} style={inp} placeholder="연락처" />
        </div>

        <div style={sectionTitle}>🏢 공급처 정보</div>
        <input name="customer_company" value={form.customer_company} onChange={handleChange} style={{...inp, marginBottom:8}} placeholder="공급처 (직접입력)" />

        <label style={lbl}>영업품목</label>
        <div style={{ display:'flex', gap:8, marginBottom:8 }}>
          <select name="product_group_id" value={form.product_group_id}
            onChange={e => setForm({ ...form, product_group_id: e.target.value, product_model_no: '', product_option: '' })}
            style={{ ...inp, flex:1 }}>
            <option value="">{groups.length===0 ? '등록된 제품군 없음' : '제품군 선택'}</option>
            {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <input name="other_product" value={form.other_product} onChange={handleChange} style={{ ...inp, flex:1 }}
            placeholder="직접입력 (기타제품)" />
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <select name="product_model_no" value={form.product_model_no} onChange={handleChange}
            style={{ ...inp, flex:1 }} disabled={!form.product_group_id || !filteredProducts.length}>
            <option value="">{!form.product_group_id ? '모델명' : (filteredProducts.length===0 ? '등록 모델 없음' : '모델명 선택')}</option>
            {filteredProducts.map(p => <option key={p.id} value={p.model_no}>{p.model_no}</option>)}
          </select>
          <select name="product_option" value={form.product_option} onChange={handleChange}
            style={{ ...inp, flex:1 }} disabled={!form.product_group_id || loadingOptions || !groupOptions.length}>
            <option value="">{loadingOptions ? '...' : (!form.product_group_id || groupOptions.length===0) ? '옵션 없음' : '옵션 선택'}</option>
            {groupOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <label style={lbl}>샘플공급 수량</label>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <input type="number" name="sample_supply" value={form.sample_supply} onChange={handleChange}
            style={{ ...inp, flex:1 }} placeholder="공급 수량 (개)" />
          <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:'#333', whiteSpace:'nowrap', cursor:'pointer' }}>
            <input type="checkbox" checked={form.sample_paid_type === '유상'}
              onChange={e => setForm({ ...form, sample_paid_type: e.target.checked ? '유상' : '무상' })} />
            유상
          </label>
        </div>
        {form.sample_paid_type === '유상' && (
          <div style={{ fontSize:12, color:'#7C3AED', marginTop:4 }}>
            💰 저장하면 출고관리에 출고 건이 자동으로 등록됩니다. (도착지·결제방법 등 나머지 정보는 출고관리에서 채워주세요)
          </div>
        )}

        <label style={lbl}>영업사항 (최초 등록 내용{editData ? ' - 수정 가능' : ''})</label>
        <textarea name="situation" value={form.situation} onChange={handleChange}
          style={{ ...inp, minHeight:70, resize:'vertical' }} placeholder="예: 5월 월례회, 샘플 공급 예정 등" />

        <label style={lbl}>
          첨부파일
          {editData && editData.file_ori && (
            <span style={{ color:'#185FA5', fontWeight:400, fontSize:13, marginLeft:8 }}>
              현재: {editData.file_ori}
            </span>
          )}
        </label>
        <FileDropZone fileName={file?.name} onFile={setFile} style={{ marginBottom:12 }} />

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'12px', background:loading?'#aaa':'#185FA5', color:'#fff',
            border:'none', borderRadius:8, fontSize:15, fontWeight:600, cursor:loading?'not-allowed':'pointer', marginTop:20 }}>
          {loading ? '저장 중...' : (editData ? '수정 완료' : '등록 완료')}
        </button>
      </div>
    </div>
  );
}

// ── 견적서(Quotation) ──────────────────────────────────────────────────
// 첨부받은 backend/templates/Quotation_Form.xlsx 서식(거래명세서 서식을 베이스로 만들어진
// "견 적 서")을 화면에서도 그대로 알아볼 수 있도록, 회의록(Tasks.js의 MeetingMinuteViewModal/
// MeetingMinutesModal)과 같은 방식 — 테두리 있는 표로 서식을 흉내낸 레이아웃 — 을 조회/작성
// 모두에 사용한다. 공급자(우리 회사) 쪽 회사명/등록번호/주소/전화는 서식 원본에 이미 고정값으로
// 인쇄돼 있는 정보라 화면에도 그대로 고정 텍스트로 보여준다(수정 불가).
// 진입 경로 2가지: ① 헤더 "📋 견적서" 버튼 → 목록(QuotationListModal)에서 새로 작성/기존 항목
// 조회·수정, ② 영업활동 상세(DetailModal)의 "📋 견적서 작성" 버튼 → 그 활동의 고객사/담당자/
// 영업품목 정보가 미리 채워진 채로 작성폼이 바로 열림(prefill).
const OUR_COMPANY = {
  name: '주식회사 나인',
  ceo:  '대표이사 김동일',
  reg:  '290 - 81 - 03083',
  addr: '울산광역시 울주군 범서읍 천상중앙길 122-1, 3층',
  tel:  'Tel. 052.937.9600',
  fax:  'Fax. 052.970.3600',
};

// 결재방법 콤보박스에 기본으로 뜨는 목록 — 목록에 없는 값도 항상 직접 입력할 수 있다(QuotationCombo).
const PAY_METHOD_OPTIONS = ['현금결재 (세금계산서 발행)', '카드결재'];

function calcQuotationTotals(items) {
  let totalQty = 0, totalSupply = 0, totalTax = 0;
  (items || []).forEach(it => {
    const qty = Number(it.quantity) || 0;
    const price = Number(it.unit_price) || 0;
    const supply = qty * price;
    const tax = Math.round(supply * 0.1);
    totalQty += qty; totalSupply += supply; totalTax += tax;
  });
  return { totalQty, totalSupply, totalTax, totalAmount: totalSupply + totalTax };
}

// 재고관리(고객사/제품군/제품명)처럼 "등록된 목록에서 선택 + 직접 입력"을 함께 지원하는
// 콤보박스. 네이티브 <input list=datalist>는 브라우저마다 팝업 모양이 제각각이라, 아래처럼
// 직접 스타일을 입힌 목록 패널을 붙여서 어디서든 동일하게 보이도록 한다. 목록에 없는 값을
// 자유롭게 입력하는 것도 항상 가능하다(목록은 "선택하면 편한" 보조수단일 뿐).
function QuotationCombo({ value, onChange, options, placeholder, inputStyle, onCommit }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const filtered = (options || []).filter(
    (o, i, arr) => arr.indexOf(o) === i && (!value || o.toLowerCase().includes(String(value).toLowerCase()))
  );

  return (
    <div ref={wrapRef} style={{ position:'relative', width:'100%' }}>
      <input
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => { if (onCommit) onCommit(value || ''); }}
        autoComplete="off"
        placeholder={placeholder}
        style={{ width:'100%', border:'none', outline:'none', fontSize:13, background:'transparent',
          padding:0, boxSizing:'border-box', ...inputStyle }}
      />
      {open && filtered.length > 0 && (
        <div style={{ position:'absolute', top:'calc(100% + 3px)', left:0, minWidth:150, maxWidth:280,
          maxHeight:190, overflowY:'auto', background:'#fff', border:'1px solid #cbd5e1', borderRadius:8,
          boxShadow:'0 8px 24px rgba(0,0,0,0.28)', zIndex:1000 }}>
          {filtered.map((opt) => (
            <div key={opt}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt); setOpen(false); }}
              style={{ padding:'7px 12px', fontSize:13, cursor:'pointer', textAlign:'left',
                background: opt === value ? '#185FA5' : '#fff', color: opt === value ? '#fff' : '#333' }}
              onMouseEnter={e => { if (opt !== value) e.currentTarget.style.background = '#f0f6ff'; }}
              onMouseLeave={e => { if (opt !== value) e.currentTarget.style.background = '#fff'; }}
            >{opt}</div>
          ))}
        </div>
      )}
    </div>
  );
}

// 서식을 흉내낸 문서 레이아웃 — readOnly=false일 때는 각 칸이 입력가능한 form 요소로 바뀐다.
// productGroups: 재고관리 제품군 목록([{id,name}, ...]) — 품목행의 "제품군" 선택창(작성/수정
// 모드 전용, 조회 화면과 실제 견적서 서식에는 "품명" 한 칸만 있으므로 영향 없음)에 쓰인다.
function QuotationDoc({ readOnly, form, setForm, items, setItems, customerOptions, customerInfoMap, onAmbiguousCustomer, productOptions, productGroups }) {
  const totals = calcQuotationTotals(items);

  // 고객사를 (일부라도) 입력/선택하면 업체관리 등록 정보로 담당자·연락처·인도장소를 자동으로 채운다.
  // - 정확히 일치하는 업체가 있으면 그 업체 정보를 바로 쓴다(업체관리 등록분이 있으면 우선, 영업활동만 여러 건이면 선택창).
  // - 정확히 일치하지 않아도 입력한 글자가 들어간 업체가 딱 1곳이면 그 업체 정보를 채운다(고객사명은 그대로 둔다).
  // 채워진 값은 모두 직접 고칠 수 있다.
  const findCustomerInfo = (v) => {
    const text = String(v || '').trim();
    if (!text || !customerInfoMap) return null;
    let key = customerInfoMap[text] ? text : null;
    if (!key && text.length >= 2) {
      const low = text.toLowerCase();
      const hits = Object.keys(customerInfoMap).filter(n => n.toLowerCase().includes(low));
      if (hits.length === 1) key = hits[0];
    }
    return key ? { key, exact: key === text, matches: customerInfoMap[key] } : null;
  };
  const applyCustomerInfo = (v, info) => {
    setForm(f => ({
      ...f, customer_company: v,
      customer_manager: info.manager || f.customer_manager,
      customer_phone: info.phone || f.customer_phone,
      delivery_place: info.address || f.delivery_place,
    }));
  };
  const handleCustomerChange = (v) => {
    const found = findCustomerInfo(v);
    if (!found) { setForm(f => ({ ...f, customer_company: v })); return; }
    const company = found.matches.find(m => m.partner_company === '업체관리 등록');
    if (company) { applyCustomerInfo(v, company); return; }
    if (found.matches.length === 1) { applyCustomerInfo(v, found.matches[0]); return; }
    setForm(f => ({ ...f, customer_company: v }));
    if (found.exact && onAmbiguousCustomer) onAmbiguousCustomer(v, found.matches);
  };
  // 입력을 마치고 칸을 벗어날 때 — 일부만 입력했어도 후보가 1곳이면 고객사명을 정식 이름으로 완성한다.
  const handleCustomerCommit = (v) => {
    const found = findCustomerInfo(v);
    if (found && !found.exact) setForm(f => (f.customer_company === v ? { ...f, customer_company: found.key } : f));
  };

  // 환경설정 > 양식관리에 "견적서" 양식이 등록돼 있으면(연동 입력란 지정) 그 양식 모양 그대로 입력/표시한다.
  const [linked, setLinked] = useState(null);
  useEffect(() => {
    let alive = true;
    API.get('/quotations/linked-form').then(r => { if (alive) setLinked(r.data && r.data.found ? r.data : false); }).catch(() => { if (alive) setLinked(false); });
    return () => { alive = false; };
  }, []);
  if (linked && linked.layout) {
    const FORM_KEYS = { customer: 'customer_company', doc_date: 'quote_date', destination: 'delivery_place', pay_method: 'pay_method', due_date: 'due_date',
      customer_manager: 'customer_manager', customer_phone: 'customer_phone', our_manager: 'our_manager', our_phone: 'our_manager_phone', note: 'note' };
    const ITEM_KEYS = { group: 'group_name', model: 'model_no', spec: 'spec', qty: 'quantity', price: 'unit_price' };
    const parseItemKey = (key) => { const m = /^item(\d+)_([a-z]+)$/.exec(key); return m ? { idx: Number(m[1]) - 1, col: m[2] } : null; };
    const fmt = (n) => (Number(n) || 0).toLocaleString();
    const get = (key) => {
      if (key === 'due_date') return form.due_date || '추후협의';
      if (key === 'doc_date') return String(form.quote_date || '').slice(0, 10);
      if (FORM_KEYS[key]) return form[FORM_KEYS[key]] || '';
      if (key === 'total_qty') return fmt(totals.totalQty);
      if (key === 'total_supply') return fmt(totals.totalSupply);
      if (key === 'total_tax') return fmt(totals.totalTax);
      if (key === 'total_amount') return fmt(totals.totalAmount);
      const ik = parseItemKey(key);
      if (ik) {
        const it = items[ik.idx];
        if (!it) return '';
        if (ik.col === 'no') return ik.idx + 1;
        const q = Number(it.quantity) || 0, p = Number(it.unit_price) || 0;
        if (ik.col === 'supply') return q && p ? fmt(q * p) : '';
        if (ik.col === 'tax') return q && p ? fmt(Math.round(q * p * 0.1)) : '';
        if (ik.col === 'qty' || ik.col === 'price') return readOnly && it[ITEM_KEYS[ik.col]] !== '' ? fmt(it[ITEM_KEYS[ik.col]]) : (it[ITEM_KEYS[ik.col]] ?? '');
        return it[ITEM_KEYS[ik.col]] || '';
      }
      return '';
    };
    const locked = (key) => /^total_/.test(key) || key === 'ship_date' || (() => { const ik = parseItemKey(key); return !!ik && ['no', 'supply', 'tax'].includes(ik.col); })()
      || !(FORM_KEYS[key] || parseItemKey(key));
    const setVal = (key, v) => {
      if (FORM_KEYS[key]) { setForm(f => ({ ...f, [FORM_KEYS[key]]: v })); return; }
      const ik = parseItemKey(key);
      if (!ik || !ITEM_KEYS[ik.col]) return;
      const field = ITEM_KEYS[ik.col];
      setItems(prev => {
        const next = prev.slice();
        while (next.length <= ik.idx) next.push({ model_no:'', group_name:'', spec:'EA', quantity:'', unit_price:'', product_id:null });
        const it = { ...next[ik.idx], [field]: (ik.col === 'qty' || ik.col === 'price') ? String(v).replace(/[^\d.]/g, '') : v };
        if (field === 'model_no') {
          const matched = (productOptions || []).find(pp => pp.model_no === v);
          it.product_id = matched ? matched.id : null;
        }
        next[ik.idx] = it;
        return next;
      });
    };
    const renderField = (key, cell, st) => {
      if (key === 'customer') {
        return (
          <QuotationCombo value={form.customer_company} options={customerOptions} inputStyle={{ ...st, fontSize: st.fontSize }}
            onChange={handleCustomerChange} onCommit={handleCustomerCommit} />
        );
      }
      if (key === 'pay_method') return <QuotationCombo value={form.pay_method} options={PAY_METHOD_OPTIONS} inputStyle={st} onChange={v => setForm(f => ({ ...f, pay_method: v }))} />;
      const ik = parseItemKey(key);
      if (ik && ik.col === 'model') {
        const it = items[ik.idx] || {};
        const names = (productOptions || []).map(pp => pp.model_no);
        return <QuotationCombo value={it.model_no} options={names} inputStyle={st} onChange={v => setVal(key, v)} />;
      }
      if (key === 'note') return <textarea value={form.note || ''} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} rows={2} style={{ ...st, resize:'vertical' }} />;
      return null;
    };
    return <LinkedFormTable layout={linked.layout} get={get} set={setVal} locked={locked} readOnly={readOnly} renderField={renderField} />;
  }

  const cellLbl = { padding:'8px 10px', fontWeight:700, fontSize:12, color:'#000', border:'1px solid #000',
    whiteSpace:'nowrap', width:84, textAlign:'center', background:'#f8fafc' };
  const cellVal = { padding:'6px 8px', fontSize:13, border:'1px solid #000', color:'#000', background:'#fff' };
  const cellValCenter = { ...cellVal, textAlign:'center' };
  const fixedVal = { ...cellVal, color:'#000' };
  const sectionHdr = { padding:'6px 10px', fontWeight:700, fontSize:13, color:'#000', border:'1px solid #000',
    textAlign:'center', background:'#e2e8f0', letterSpacing:4 };
  const thStyle = { padding:'7px 6px', fontSize:12, fontWeight:700, border:'1px solid #000', background:'#f1f5f9', textAlign:'center', color:'#000' };
  const tdStyle = { padding:'6px 6px', fontSize:12, border:'1px solid #000', textAlign:'center', color:'#000' };
  const inputBare = { width:'100%', border:'none', outline:'none', fontSize:13, background:'transparent', padding:0, boxSizing:'border-box', color:'#000' };
  const inputTd   = { width:'100%', border:'none', outline:'none', fontSize:12, background:'transparent', padding:0, boxSizing:'border-box', textAlign:'center', color:'#000' };
  // 담당자/연락처 두 칸(고객사·공급자 담당자 행)은 상세보기(읽기전용 span)와 작성/수정(input)
  // 화면이 완전히 같은 위치에 보이도록, 두 칸 너비를 이 상수 하나로 고정해서 함께 쓴다
  // (예전에는 span은 텍스트 길이만큼만 차지하고 input은 브라우저 기본 폭을 차지해서, 같은
  // justifyContent:'center' 코드인데도 상세보기에서는 이름·연락처가 중앙에 바짝 붙어 보이고
  // 작성/수정에서는 더 벌어져 보이는 차이가 있었다).
  const managerFieldWidth = 130;
  const managerFieldStyle = { display:'inline-block', width:managerFieldWidth, textAlign:'center' };
  const managerInputStyle = { ...inputBare, width:managerFieldWidth, flex:'0 0 auto', textAlign:'center' };

  // 재고관리 제품군 id → 이름 매핑 (품목행의 제품명 콤보를 선택된 제품군으로 좁혀서 보여줄 때 씀)
  const groupNameById = {};
  (productGroups || []).forEach(g => { groupNameById[g.id] = g.name; });
  const groupNameOptions = (productGroups || []).map(g => g.name);
  // 특정 제품군을 골랐으면 그 제품군에 속한 제품명만, 아니면(직접 입력한 제품군 등) 전체 제품명을
  // 후보로 보여준다 — 어느 쪽이든 목록에 없는 이름을 직접 입력하는 것도 항상 가능하다.
  const productNamesFor = (groupName) => {
    const list = productOptions || [];
    const matched = groupName ? list.filter(p => groupNameById[p.group_id] === groupName) : list;
    return matched.map(p => p.model_no);
  };

  const setF = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const setItem = (idx, key) => (e) => {
    const val = e.target.value;
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };
  // 콤보박스(제품군/제품명)는 값을 문자열로 바로 넘겨주므로 event가 아닌 원시값을 받는다.
  const setItemVal = (idx, key, val) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      if (key === 'model_no') {
        // 재고관리 제품(products) 목록의 제품명과 정확히 일치하면 그 제품의 id(및 제품군)를 함께
        // 저장해서 견적서 저장 시 "제품군"을 파일명에 반영할 수 있게 한다. 직접 입력이면 product_id는 null.
        const matched = (productOptions || []).find(p => p.model_no === val);
        return {
          ...it, model_no: val,
          product_id: matched ? matched.id : null,
          group_name: matched ? (groupNameById[matched.group_id] || it.group_name || '') : it.group_name,
        };
      }
      if (key === 'group_name') return { ...it, group_name: val };
      return { ...it, [key]: val };
    }));
  };
  const addItem = () => setItems(prev => [...prev, { model_no:'', group_name:'', spec:'EA', quantity:'', unit_price:'', product_id:null }]);
  const removeItem = (idx) => setItems(prev => prev.length > 1 ? prev.filter((_,i)=>i!==idx) : prev);

  return (
    <div style={{ border:'2px solid #000', padding:16, background:'#fff' }}>
      <div style={{ textAlign:'center', fontSize:22, fontWeight:800, letterSpacing:10, padding:'6px 0 14px' }}>견 적 서</div>

      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:14, tableLayout:'fixed' }}>
        <colgroup>
          <col style={{ width:84 }} />
          <col />
          <col style={{ width:84 }} />
          <col />
        </colgroup>
        <tbody>
          <tr>
            <td colSpan={2} style={sectionHdr}>고 객 사</td>
            <td colSpan={2} style={sectionHdr}>공 급 자</td>
          </tr>
          <tr>
            <td style={cellLbl}>회사명</td>
            <td style={cellVal}>
              {readOnly ? (
                <>{form.customer_company || '-'}{form.customer_company ? ' 귀하' : ''}</>
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <QuotationCombo value={form.customer_company}
                      onChange={handleCustomerChange} onCommit={handleCustomerCommit}
                      options={customerOptions}
                      placeholder="고객사명 (영업활동에 등록된 회사 선택 또는 직접입력)" />
                  </div>
                  {form.customer_company && <span style={{ whiteSpace:'nowrap' }}>귀하</span>}
                </div>
              )}
            </td>
            <td style={cellLbl}>회사명</td>
            <td style={fixedVal}>
              <span>{OUR_COMPANY.name}</span>
              <span style={{ marginLeft:28 }}>{OUR_COMPANY.ceo}</span>
            </td>
          </tr>
          <tr>
            <td style={cellLbl}>견적일자</td>
            <td style={cellVal}>
              {readOnly ? (form.quote_date || '-') :
                <input type="date" value={form.quote_date} onChange={setF('quote_date')} style={inputBare} />}
            </td>
            <td style={cellLbl}>등록번호</td>
            <td style={fixedVal}>{OUR_COMPANY.reg}</td>
          </tr>
          <tr>
            <td style={cellLbl}>인도장소</td>
            <td style={cellVal}>
              {readOnly ? (form.delivery_place || '-') :
                <input value={form.delivery_place} onChange={setF('delivery_place')} style={inputBare} placeholder="인도장소" />}
            </td>
            <td style={cellLbl}>주소</td>
            <td style={fixedVal}>{OUR_COMPANY.addr}</td>
          </tr>
          <tr>
            <td style={cellLbl}>결재방법</td>
            <td style={cellVal}>
              {readOnly ? (form.pay_method || '-') :
                <QuotationCombo value={form.pay_method}
                  onChange={v => setForm(f => ({ ...f, pay_method: v }))}
                  options={PAY_METHOD_OPTIONS}
                  placeholder="결재방법 선택 또는 직접입력" />}
            </td>
            <td style={cellLbl}>전화</td>
            <td style={fixedVal}>{OUR_COMPANY.tel} / {OUR_COMPANY.fax}</td>
          </tr>
          <tr>
            <td style={cellLbl}>담당자</td>
            <td style={cellValCenter}>
              {readOnly ? (
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <span style={managerFieldStyle}>{form.customer_manager || '-'}</span>
                  <span style={managerFieldStyle}>{form.customer_phone || ''}</span>
                </div>
              ) : (
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <input value={form.customer_manager} onChange={setF('customer_manager')} style={managerInputStyle} placeholder="직급 이름" />
                  <input value={form.customer_phone} onChange={setF('customer_phone')} style={managerInputStyle} placeholder="연락처" />
                </div>
              )}
            </td>
            <td style={cellLbl}>담당자</td>
            <td style={cellValCenter}>
              {readOnly ? (
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <span style={managerFieldStyle}>{form.our_manager || '-'}</span>
                  <span style={managerFieldStyle}>{form.our_manager_phone || ''}</span>
                </div>
              ) : (
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <input value={form.our_manager} onChange={setF('our_manager')} style={managerInputStyle} placeholder="비워두면 '-'로 표시됩니다" />
                  <input value={form.our_manager_phone} onChange={setF('our_manager_phone')} style={managerInputStyle} placeholder="연락처" />
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td style={cellLbl}>견적금액</td>
            <td colSpan={3} style={{ ...cellVal, fontWeight:700, fontSize:15 }}>
              ￦ {totals.totalAmount.toLocaleString()} <span style={{ fontWeight:400, fontSize:12, color:'#000' }}>(공급가액 + 세액)</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:14 }}>
      <table style={{ flex:1, minWidth:0, borderCollapse:'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width:40 }}>No</th>
            {readOnly ? (
              <th style={thStyle}>품    명</th>
            ) : (
              <>
                <th style={{ ...thStyle, width:120 }}>제품군</th>
                <th style={thStyle}>제 품 명</th>
              </>
            )}
            <th style={{ ...thStyle, width:70 }}>규 격</th>
            <th style={{ ...thStyle, width:70 }}>수 량</th>
            <th style={{ ...thStyle, width:100 }}>단 가</th>
            <th style={{ ...thStyle, width:110 }}>공급가액</th>
            <th style={{ ...thStyle, width:90 }}>세 액</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => {
            const qty = Number(it.quantity) || 0;
            const price = Number(it.unit_price) || 0;
            const supply = qty * price;
            const tax = Math.round(supply * 0.1);
            return (
              <tr key={idx}>
                <td style={tdStyle}>{idx + 1}</td>
                {readOnly ? (
                  <td style={{ ...tdStyle, textAlign:'left' }}>{it.model_no}</td>
                ) : (
                  <>
                    <td style={{ ...tdStyle, textAlign:'left' }}>
                      <QuotationCombo value={it.group_name}
                        onChange={v => setItemVal(idx, 'group_name', v)}
                        options={groupNameOptions}
                        placeholder="제품군" />
                    </td>
                    <td style={{ ...tdStyle, textAlign:'left' }}>
                      <QuotationCombo value={it.model_no}
                        onChange={v => setItemVal(idx, 'model_no', v)}
                        options={productNamesFor(it.group_name)}
                        placeholder="제품명 선택 또는 직접입력" />
                    </td>
                  </>
                )}
                <td style={tdStyle}>
                  {readOnly ? (it.spec || 'EA') :
                    <input value={it.spec} onChange={setItem(idx,'spec')} style={inputTd} />}
                </td>
                <td style={tdStyle}>
                  {readOnly ? qty.toLocaleString() :
                    <input type="number" value={it.quantity} onChange={setItem(idx,'quantity')} style={inputTd} />}
                </td>
                <td style={tdStyle}>
                  {readOnly ? price.toLocaleString() :
                    <input type="number" value={it.unit_price} onChange={setItem(idx,'unit_price')} style={inputTd} />}
                </td>
                <td style={tdStyle}>{supply.toLocaleString()}</td>
                <td style={{ ...tdStyle, position:'relative' }}>
                  {tax.toLocaleString()}
                  {!readOnly && items.length > 1 && (
                    <button type="button" onClick={() => removeItem(idx)} title="이 품목 삭제"
                      style={{ position:'absolute', top:-1, right:-1, width:16, height:16, lineHeight:'14px',
                        background:'#dc2626', color:'#fff', border:'1px solid #fff', borderRadius:'50%',
                        fontSize:10, cursor:'pointer', padding:0 }}>✕</button>
                  )}
                </td>
              </tr>
            );
          })}
          <tr>
            <td colSpan={readOnly ? 3 : 4} style={{ ...tdStyle, fontWeight:700 }}>합 계</td>
            <td style={{ ...tdStyle, fontWeight:700 }}>{totals.totalQty.toLocaleString()}</td>
            <td style={{ ...tdStyle, fontWeight:700 }}>-</td>
            <td style={{ ...tdStyle, fontWeight:700 }}>{totals.totalSupply.toLocaleString()}</td>
            <td style={{ ...tdStyle, fontWeight:700 }}>{totals.totalTax.toLocaleString()}</td>
          </tr>
        </tbody>
      </table>
      {!readOnly && (
        <button type="button" onClick={addItem} title="품목 추가"
          style={{ flexShrink:0, width:34, height:34, marginTop:2, background:'#0f2942', color:'#fff',
            border:'none', borderRadius:8, fontSize:19, fontWeight:700, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center' }}>
          +
        </button>
      )}
      </div>

      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <tbody>
          <tr>
            <td style={{ ...cellLbl, width:84 }}>기타사항</td>
            <td style={{ ...cellVal, minHeight:60 }}>
              {readOnly ? (
                <div style={{ minHeight:50, whiteSpace:'pre-wrap' }}>{form.note || '-'}</div>
              ) : (
                <textarea value={form.note} onChange={setF('note')} rows={3}
                  style={{ ...inputBare, resize:'vertical', minHeight:50 }} placeholder="특이사항, 유효기간 등" />
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── 견적서 목록/작성/수정/조회 모달 (헤더 "📋 견적서" 버튼) ──────────────────
export function QuotationListModal({ user, canRegister, canUpdate, canDelete, onClose, onChanged }) {
  const [list, setList]     = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null); // null = 신규 작성
  const [viewId, setViewId]     = useState(null);
  const [msg, setMsg]           = useState('');

  const load = () => API.get('/quotations').then(r => setList(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  const showMsg = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 2500); };

  const filtered = list.filter(q =>
    !search ||
    (q.customer_company||'').includes(search) ||
    (q.top_model_no||'').includes(search) ||
    (q.created_by||'').includes(search)
  );

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:1080, maxWidth:'96vw', maxHeight:'90vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <h3 style={{ fontSize:17, fontWeight:600, margin:0 }}>📋 견적서</h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {canRegister && (
              <button className="hdr-btn" onClick={() => { setEditData(null); setShowForm(true); }}>
                + 새 견적서 작성
              </button>
            )}
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        {msg && <div style={{ background:'#d1fae5', color:'#065f46', padding:'8px 14px', borderRadius:8, marginBottom:12, fontSize:13 }}>{msg}</div>}

        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 고객사, 대표품목, 작성자 검색..."
          style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:13, boxSizing:'border-box', marginBottom:12 }} />

        <div style={{ border:'1px solid #e5e7eb', borderRadius:10, overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'85px 1.3fr 85px 1.2fr 105px 120px 190px',
            padding:'9px 12px', background:'#f8fafc', fontSize:13, fontWeight:600, color:'#666', borderBottom:'1px solid #e5e7eb', textAlign:'center' }}>
            <span>일자</span><span>고객사</span><span>담당자</span><span>대표품목</span><span>합계금액</span><span>작성자</span><span>업데이트일시</span>
          </div>
          {filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'40px 0', color:'#aaa', fontSize:13 }}>등록된 견적서가 없습니다.</div>
          ) : filtered.map(q => {
            // "업데이트일시" 칸: 수정된 적이 있으면 마지막 수정 시각·수정자를, 없으면 최초 등록
            // 시각·작성자를 보여준다(부서를 뺀 "직급 이름"만 써서 한 줄에 다 들어가게 한다).
            const latestAt   = (q.updated_at || q.created_at || '').slice(0,16) || '-';
            const latestWho  = (q.updated_at ? q.updated_by_short : q.created_by_short) || q.created_by || '-';
            return (
              <div key={q.id} onClick={() => setViewId(q.id)}
                style={{ display:'grid', gridTemplateColumns:'85px 1.3fr 85px 1.2fr 105px 120px 190px',
                  padding:'10px 12px', fontSize:13, borderBottom:'1px solid #f0f0f0', alignItems:'center', cursor:'pointer', textAlign:'center' }}
                onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <span style={{ color:'#666', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{q.quote_date || '-'}</span>
                <span style={{ fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{q.customer_company || '-'}</span>
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{q.customer_manager || '-'}</span>
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{q.top_model_no || '-'}</span>
                <span style={{ fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{Number(q.total_amount||0).toLocaleString()}</span>
                <span style={{ color:'#888', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{q.created_by_short || q.created_by || '-'}</span>
                <span style={{ color:'#888', fontSize:12, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{latestAt} ({latestWho})</span>
              </div>
            );
          })}
        </div>
      </div>

      {showForm && (
        <QuotationFormModal
          editData={editData}
          user={user}
          onClose={() => { setShowForm(false); setEditData(null); }}
          onSaved={() => {
            const wasEdit = !!editData;
            setShowForm(false); setEditData(null); load();
            showMsg(wasEdit ? '수정 완료!' : '등록 완료!');
            if (onChanged) onChanged();
          }}
        />
      )}

      {viewId && (
        <QuotationViewModal
          id={viewId}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onClose={() => setViewId(null)}
          onEdit={(q) => { setViewId(null); setEditData(q); setShowForm(true); }}
          onDeleted={() => {
            setViewId(null); load(); showMsg('삭제 완료!');
            if (onChanged) onChanged();
          }}
        />
      )}
    </div>
  );
}

// ── 견적서 상세 조회 (읽기 전용 — 서식 그대로) ────────────────────────────
function QuotationViewModal({ id, canUpdate, canDelete, onClose, onEdit, onDeleted }) {
  const [data, setData] = useState(null);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => { API.get('/quotations/'+id).then(r => setData(r.data)).catch(()=>{}); }, [id]);
  useEffect(() => { API.get(`/quotations/${id}/attachments`).then(r => setAttachments(r.data || [])).catch(()=>{}); }, [id]);

  // 첨부파일 개별 다운로드 — Companies.js 등과 동일하게 인증 토큰이 실린 blob 요청으로 받아서
  // 강제 다운로드시킨다(plain <a href>는 로그인 토큰을 실어보낼 수 없음).
  const handleDownloadAttachment = async (att) => {
    try {
      const res = await API.get(`/quotations/${id}/attachments/${att.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = att.file_ori || 'attachment';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('첨부파일을 다운로드할 수 없습니다.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('이 견적서를 삭제하시겠습니까?')) return;
    try {
      await API.delete('/quotations/'+id);
      onDeleted();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    }
  };

  // 내보내기 — 회의록 상세와 동일하게, 저장된 엑셀 원본 파일을 그대로 다운로드한다.
  const handleExport = async () => {
    if (!data?.file_path) { alert('아직 저장된 엑셀 파일이 없습니다.'); return; }
    try {
      const res = await API.get(`/quotations/${id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const exportFileName = `${(data.customer_company || '견적서').replace(/[\\/:*?"<>|]/g,'_')}_견적서.xlsx`;
      a.download = exportFileName;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      logDownload('견적서', exportFileName);
    } catch (err) {
      alert('파일을 다운로드할 수 없습니다.');
    }
  };

  // 인쇄 — 회의록 상세와 동일하게, 저장된 PDF를 숨겨진 iframe에 띄운 뒤 자동으로 인쇄창을 연다.
  const handlePrint = async () => {
    try {
      const res = await API.get(`/quotations/${id}/pdf?print=1`, { responseType: 'blob' }); // 인쇄: 작성자 → 출력자 표기
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
      iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = 'none';
      iframe.src = blobUrl;
      // PDF뷰어가 fit-to-page 계산을 마칠 시간을 준 뒤 인쇄를 호출한다(다른 화면들과 동일한 이유).
      iframe.onload = () => { setTimeout(() => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch (e) {} }, 600); };
      document.body.appendChild(iframe);
      logPrint('견적서', data?.customer_company || '견적서');
    } catch (err) {
      alert('인쇄용 PDF를 만드는 중 오류가 발생했습니다.');
    }
  };

  if (!data) return null;

  const form = data;
  const items = data.items || [];

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.55)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:980, maxWidth:'96vw', maxHeight:'92vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>견적서 상세</h3>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {canUpdate && (
              <button className="hdr-btn" onClick={() => onEdit(data)}>
                ✏️ 수정
              </button>
            )}
            {canDelete && (
              <button className="hdr-btn" onClick={handleDelete}>
                🗑 삭제
              </button>
            )}
            <button className="hdr-btn" onClick={handleExport}>
              📥 내보내기
            </button>
            <button className="hdr-btn" onClick={handlePrint}>
              🖨️ 인쇄
            </button>
            <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
          </div>
        </div>

        <QuotationDoc readOnly form={form} setForm={()=>{}} items={items} setItems={()=>{}} customerOptions={[]} productOptions={[]} />

        {/* 첨부파일(PDF/이미지) — "🖨️ 인쇄" 시 이 견적서 다음 페이지부터 원본 형태로 이어붙여서
            나온다(routes/quotations.js GET /:id/pdf). 여기서는 원본 확인/다운로드용으로만 목록을 보여준다. */}
        {attachments.length > 0 && (
          <div style={{ marginTop:12 }}>
            <div style={{ fontSize:12, color:'#888', marginBottom:6 }}>
              📎 첨부파일 {attachments.length}건 — 인쇄 시 다음 페이지에 원본 형태로 포함됩니다
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {attachments.map(att => (
                <div key={att.id} onClick={() => handleDownloadAttachment(att)}
                  style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    padding:'7px 10px', background:'#f8fafc', border:'1px solid #eee', borderRadius:6,
                    fontSize:12, cursor:'pointer' }}>
                  <span>📄 {att.file_ori}</span>
                  <span style={{ color:'#185FA5' }}>다운로드</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 작성일(좌)·작성자(우) 한 줄에 표시. 작성자는 부서명을 빼고 "직급 이름"만
            보여준다(created_by_short/updated_by_short — 목록의 "작성자" 칸과 동일한 짧은 형식).
            수정 이력이 있으면 마지막으로 수정한 사람/시각을, 없으면 최초 작성자/작성시각을 쓴다. */}
        <div style={{ fontSize:12, color:'#000', marginTop:10, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>작성일 : {(data.created_at||'').slice(0,16) || '-'}</div>
          <div>
            작성자 : {data.updated_at
              ? `${data.updated_by_short || data.updated_by || '-'} (${(data.updated_at||'').slice(0,16)})`
              : `${data.created_by_short || data.created_by || '-'} (${(data.created_at||'').slice(0,16)})`}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 견적서 작성/수정 (서식 그대로 입력 가능한 형태) ──────────────────────────
// prefill: 영업활동 상세의 "견적서 작성" 버튼으로 들어온 경우, 그 활동의 고객사/담당자/영업품목
// 정보를 미리 채워준다({ customer_company, customer_manager, customer_phone, model_no }).
// editData가 있으면(수정) prefill은 무시한다.
function QuotationFormModal({ editData, prefill, user, onClose, onSaved }) {
  const [form, setForm] = useState({
    // 영업활동 상세의 "견적서 작성" 버튼으로 들어온 경우에만 그 활동의 id가 실려 있다 — 저장 시
    // 함께 보내서, 서버가 그 활동의 "영업사항 업데이트 이력"에 견적서 등록/수정 사실을 자동으로
    // 남길 수 있게 한다(견적서 헤더 메뉴에서 직접 작성한 경우는 activity_id가 없어 남기지 않음).
    activity_id:        editData?.activity_id           || prefill?.activity_id || null,
    quote_date:        editData?.quote_date        || getKstDateString(),
    customer_company:  editData?.customer_company   || prefill?.customer_company || '',
    // 인도장소/결재방법은 새로 작성할 때 자주 쓰는 값을 기본값으로 미리 채워둔다(수정 화면은
    // 저장된 값을 그대로 씀 — editData가 있으면 그 값이 항상 우선).
    delivery_place:    editData?.delivery_place     || '고객 지정장소',
    pay_method:        editData?.pay_method         || '현금결재 (세금계산서 발행)',
    due_date:          editData?.due_date           || '추후협의',
    customer_manager:  editData?.customer_manager   || prefill?.customer_manager || '',
    customer_phone:    editData?.customer_phone     || prefill?.customer_phone   || '',
    // 담당자는 기본적으로 작성하는 계정 본인("직급 이름")이 기본값이지만 수정 가능하고,
    // 비워두는 것도 허용된다(비우면 서식/화면에 "-"로 표시됨).
    our_manager:       editData ? (editData.our_manager || '') : [user?.position, user?.name].filter(Boolean).join(' '),
    our_manager_phone: editData ? (editData.our_manager_phone || '') : (user?.phone || ''),
    note:              editData?.note               || '',
  });
  const [items, setItems] = useState(
    editData?.items?.length
      ? editData.items.map(it => ({ product_id: it.product_id, model_no: it.model_no, group_name:'', spec: it.spec || 'EA', quantity: it.quantity, unit_price: it.unit_price }))
      : [{ product_id:null, model_no: prefill?.model_no || '', group_name:'', spec:'EA', quantity:'', unit_price:'' }]
  );
  const [products, setProducts] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerInfoMap, setCustomerInfoMap] = useState({});
  // 고객사가 여러 영업활동(다른 영업사)에 걸쳐 중복 등록돼 있어서 어느 담당자 정보를 가져와야
  // 할지 모호할 때, 곧바로 채우는 대신 이 상태에 후보를 담아 "영업사 선택창"을 띄운다.
  const [customerChoice, setCustomerChoice] = useState(null); // { company, matches } | null
  const [loading, setLoading] = useState(false);
  // 첨부파일(PDF/이미지) — 이미 저장된 견적서(수정 화면)에 등록돼 있는 첨부는 attachments에,
  // 아직 저장되지 않은 신규 작성 화면에서 미리 담아둔 파일은 pendingFiles에 둔다. 신규 작성은
  // 견적서 자체가 아직 서버에 없어(quotation_id가 없음) 바로 업로드할 수 없으므로, 견적서
  // 저장(POST)이 끝나 id가 생긴 직후에 한꺼번에 업로드한다(handleSubmit 참고).
  const [attachments, setAttachments] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [attBusy, setAttBusy] = useState(false);

  useEffect(() => {
    if (editData?.id) {
      API.get(`/quotations/${editData.id}/attachments`).then(r => setAttachments(r.data || [])).catch(() => {});
    }
  }, [editData]);

  // 견적서 첨부는 인쇄 시 그대로 이어붙일 수 있는 PDF/이미지(JPG·PNG)만 허용한다(서버도 동일하게
  // 검증 — utils/quotationAttachments.js).
  const ATTACH_EXT_RE = /\.(pdf|jpe?g|png)$/i;
  const handlePickAttachments = async (files) => {
    const valid = files.filter(f => ATTACH_EXT_RE.test(f.name));
    if (valid.length !== files.length) alert('PDF 또는 이미지(JPG/PNG) 파일만 첨부할 수 있습니다.');
    if (!valid.length) return;
    if (editData?.id) {
      // 이미 저장된(수정 중인) 견적서 — 바로 업로드
      setAttBusy(true);
      try {
        const fd = new FormData();
        valid.forEach(f => fd.append('files', f));
        await API.post(`/quotations/${editData.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const list = await API.get(`/quotations/${editData.id}/attachments`);
        setAttachments(list.data || []);
      } catch (err) {
        alert(err.response?.data?.message || '첨부파일 업로드 중 오류가 발생했습니다.');
      } finally {
        setAttBusy(false);
      }
    } else {
      // 신규 작성 중 — 아직 견적서가 저장되지 않아 업로드할 수 없으므로 담아만 두고,
      // 실제 업로드는 견적서 저장이 끝난 뒤(handleSubmit)에 한다.
      setPendingFiles(prev => [...prev, ...valid]);
    }
  };
  const handleDeleteAttachment = async (attId) => {
    if (!editData?.id) return;
    if (!window.confirm('이 첨부파일을 삭제하시겠습니까?')) return;
    try {
      await API.delete(`/quotations/${editData.id}/attachments/${attId}`);
      setAttachments(prev => prev.filter(a => a.id !== attId));
    } catch (err) {
      alert('삭제 중 오류가 발생했습니다.');
    }
  };
  const handleRemovePendingFile = (idx) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // 제품명 선택 목록: 재고관리에 등록된 완제품(products). 이 계정이 재고관리 읽기 권한이 없으면
  // 조용히 실패시키고 직접입력만 가능하게 둔다.
  useEffect(() => {
    API.get('/stock/products').then(r => setProducts(r.data)).catch(() => {});
  }, []);

  // 제품군 선택 목록: 재고관리 제품군(안전벨트/안전블럭 등) 탭 목록.
  useEffect(() => {
    API.get('/stock/groups').then(r => setGroupOptions(r.data)).catch(() => {});
  }, []);

  // 고객사 선택 목록: 영업활동(영업관리)에 이미 등록된 고객사 + 업체관리(companies)에 등록된
  // 고객사, 두 곳에서 모두 뽑아온다(2026-08 — "견적서 작성시 업체관리에 등록된 고객사도 가져올
  // 수 있게" 요청). 목록에서 회사를 선택(또는 정확한 이름을 직접 입력)하면 그 담당자/연락처를
  // 견적서에 자동으로 채워주기 위해, 이름 목록과 함께 "회사명 → 후보(영업활동 건 + 업체관리 등록
  // 정보) 목록" 맵도 만들어둔다. 같은 고객사가 여러 영업활동에 등록돼 있거나 업체관리에도 등록돼
  // 있으면(담당자가 다를 수 있음) 후보를 전부 배열로 모아두고, 1건이면 바로 채우고 2건 이상이면
  // 영업사 선택창을 띄운다(onAmbiguousCustomer, QuotationDoc 참고). 업체관리 등록 정보를 가장
  // 최신/신뢰도 높은 정보로 보고 후보 맨 위에 오도록 하고, 그다음 최근 영업활동 순으로 정렬한다.
  // 업체관리 조회는 별도 메뉴 권한 없이도 쓸 수 있는 전용 라우트를 쓴다(companies.js 참고).
  useEffect(() => {
    Promise.all([
      API.get('/sales-activities').catch(() => ({ data: [] })),
      API.get('/companies/customers-lookup').catch(() => ({ data: [] })),
    ]).then(([actRes, compRes]) => {
      const rows = actRes.data || [];
      const companies = compRes.data || [];
      const map = {};
      rows.forEach(a => {
        if (!a.customer_company) return;
        if (!map[a.customer_company]) map[a.customer_company] = [];
        map[a.customer_company].push({
          id: a.id,
          partner_company: a.partner_company || '',
          manager: [a.customer_position, a.customer_manager].filter(Boolean).join(' '),
          phone: a.customer_phone || '',
          activity_date: a.activity_date || '',
        });
      });
      companies.forEach(c => {
        if (!c.company_name) return;
        if (!map[c.company_name]) map[c.company_name] = [];
        // 업체관리의 "담당자" 칸은 자유 텍스트라 "홍길동 과장"처럼 이름과 직급이 함께 입력돼
        // 있는 경우가 많다. 견적서의 담당자 칸은 "직급 이름" 한 칸으로 합쳐서 쓰므로(다른
        // 영업활동 후보들과 형식을 맞추기 위해), parseManagerTitle로 직급 키워드를 찾아
        // [직급, 이름] 순서로 다시 조합한다(직급을 못 찾으면 이름만 남는다).
        const { name: mgrName, position: mgrPosition } = parseManagerTitle(c.manager_name);
        // 업체관리 후보는 가장 위에 오도록 다른 어떤 영업활동 id보다도 큰 값을 준다(정렬은
        // id 내림차순이므로 값이 클수록 먼저 옴). 실제 견적서 저장 데이터가 아니므로 별도 접두어.
        map[c.company_name].unshift({
          id: Number.MAX_SAFE_INTEGER - c.id,
          partner_company: '업체관리 등록',
          manager: [mgrPosition, mgrName].filter(Boolean).join(' '),
          phone: c.manager_contact || '',
          address: c.biz_address || '',
          activity_date: c.ceo_name ? `대표 ${c.ceo_name}` : '',
        });
      });
      const names = [...new Set([...companies.map(c => c.company_name), ...rows.map(a => a.customer_company)].filter(Boolean))];
      setCustomerOptions(names);
      Object.keys(map).forEach(k => map[k].sort((a,b) => (b.id||0) - (a.id||0)));
      setCustomerInfoMap(map);
    });
  }, []);

  // 수정(editData) 화면 진입 시: 이미 저장된 품목은 product_id만 있고 제품군 이름은 없으므로,
  // 제품/제품군 목록이 로드되면 product_id → group_id → 제품군 이름을 역추적해서 "제품군"
  // 콤보박스에도 미리 채워준다(품목추가 시점부터 있던 항목이 아니면 건드리지 않음).
  useEffect(() => {
    if (!products.length || !groupOptions.length) return;
    setItems(prev => prev.map(it => {
      if (it.group_name || !it.product_id) return it;
      const p = products.find(pp => pp.id === it.product_id);
      if (!p) return it;
      const g = groupOptions.find(gg => gg.id === p.group_id);
      return g ? { ...it, group_name: g.name } : it;
    }));
  }, [products, groupOptions]);

  const handleSubmit = async () => {
    if (!form.customer_company.trim()) return alert('고객사를 입력해주세요.');
    const cleanItems = items.filter(it => (it.model_no||'').trim());
    if (!cleanItems.length) return alert('품목을 1개 이상 입력해주세요.');
    setLoading(true);
    try {
      const payload = { ...form, items: cleanItems };
      let quotationId = editData?.id;
      if (editData) {
        await API.put('/quotations/'+editData.id, payload);
      } else {
        const res = await API.post('/quotations', payload);
        quotationId = res.data.id;
      }
      // 신규 작성 화면에서 저장 전에 미리 담아둔 첨부파일이 있으면, 방금 만들어진 견적서 id로
      // 이제 업로드한다(수정 화면은 파일을 고르는 즉시 바로 업로드하므로 여기 올 일이 없음).
      if (!editData && pendingFiles.length && quotationId) {
        try {
          const fd = new FormData();
          pendingFiles.forEach(f => fd.append('files', f));
          await API.post(`/quotations/${quotationId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (attErr) {
          // 견적서 자체는 이미 저장되었으므로, 첨부파일 업로드 실패는 안내만 하고 저장은 그대로 완료 처리한다.
          alert('견적서는 저장되었지만 첨부파일 업로드 중 오류가 발생했습니다. 견적서를 다시 열어 첨부파일을 추가해주세요.');
        }
      }
      onSaved();
    } catch (err) {
      alert(err.response?.data?.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.55)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2100 }}>
      <div style={{ background:'#fff', borderRadius:16, padding:24, width:980, maxWidth:'96vw', maxHeight:'92vh', overflowY:'auto' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <h3 style={{ fontSize:16, fontWeight:600, margin:0 }}>{editData ? '견적서 수정' : '견적서 작성'}</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999' }}>✕</button>
        </div>

        <QuotationDoc readOnly={false} form={form} setForm={setForm} items={items} setItems={setItems}
          customerOptions={customerOptions} customerInfoMap={customerInfoMap}
          onAmbiguousCustomer={(company, matches) => setCustomerChoice({ company, matches })}
          productOptions={products} productGroups={groupOptions} />

        {/* 첨부파일(PDF/이미지) — 인쇄 시 견적서 다음 페이지부터 원본 형태로 이어붙여서 보여준다
            (routes/quotations.js GET /:id/pdf + utils/quotationAttachments.js). */}
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:13, fontWeight:600, color:'#333', marginBottom:6 }}>
            📎 첨부파일(PDF/이미지) <span style={{ fontWeight:400, color:'#888' }}>— 인쇄 시 견적서 다음 페이지에 원본 형태로 포함됩니다</span>
          </div>
          <FileDropZone multiple accept=".pdf,.jpg,.jpeg,.png" onFiles={handlePickAttachments}
            fileNames={[...attachments.map(a => a.file_ori), ...pendingFiles.map(f => f.name)]} />
          {(attachments.length > 0 || pendingFiles.length > 0) && (
            <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:12, marginTop:-8 }}>
              {attachments.map(att => (
                <div key={att.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'7px 10px', background:'#f8fafc', border:'1px solid #eee', borderRadius:6, fontSize:12 }}>
                  <span>📄 {att.file_ori}</span>
                  <button onClick={() => handleDeleteAttachment(att.id)}
                    style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:12 }}>삭제</button>
                </div>
              ))}
              {pendingFiles.map((f, idx) => (
                <div key={idx} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'7px 10px', background:'#f0f6ff', border:'1px solid #cfe0f5', borderRadius:6, fontSize:12 }}>
                  <span>📎 {f.name} <span style={{ color:'#888' }}>(저장 시 업로드됩니다)</span></span>
                  <button onClick={() => handleRemovePendingFile(idx)}
                    style={{ background:'none', border:'none', color:'#dc2626', cursor:'pointer', fontSize:12 }}>삭제</button>
                </div>
              ))}
            </div>
          )}
          {attBusy && <div style={{ fontSize:12, color:'#888', marginBottom:8 }}>업로드 중...</div>}
        </div>

        <button onClick={handleSubmit} disabled={loading}
          style={{ width:'100%', padding:'12px', background:loading?'#aaa':'#185FA5', color:'#fff',
            border:'none', borderRadius:8, fontSize:15, fontWeight:600, cursor:loading?'not-allowed':'pointer', marginTop:16 }}>
          {loading ? '저장 중...' : (editData ? '수정 완료' : '견적서 저장')}
        </button>
      </div>
    </div>

    {/* 영업사 선택창 — 같은 고객사가 서로 다른 영업활동(영업사)에 여러 번 등록돼 있어서 담당자
        정보를 자동으로 하나로 정할 수 없을 때만 뜬다. 고르면 그 건의 담당자/연락처만 채워주고
        닫힌다(고객사명은 이미 입력돼 있으므로 그대로 둠). */}
    {customerChoice && (
      <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.55)',
        display:'flex', alignItems:'center', justifyContent:'center', zIndex:2200 }}>
        <div style={{ background:'#fff', borderRadius:14, padding:20, width:380, maxWidth:'90vw' }}
          onClick={e => e.stopPropagation()}>
          <div style={{ fontSize:15, fontWeight:600, marginBottom:4 }}>영업사 선택</div>
          <div style={{ fontSize:12, color:'#888', marginBottom:12 }}>
            "{customerChoice.company}"이(가) 여러 영업활동에 등록되어 있습니다. 담당자 정보를 가져올 영업사를 선택해주세요.
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:280, overflowY:'auto' }}>
            {customerChoice.matches.map(m => (
              <button key={m.id} onClick={() => {
                setForm(f => ({ ...f, customer_manager: m.manager || f.customer_manager, customer_phone: m.phone || f.customer_phone, delivery_place: m.address || f.delivery_place }));
                setCustomerChoice(null);
              }} style={{ textAlign:'left', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8,
                  background:'#f8fafc', cursor:'pointer', fontSize:13 }}
                onMouseEnter={e => e.currentTarget.style.background='#eef4fb'}
                onMouseLeave={e => e.currentTarget.style.background='#f8fafc'}>
                <div style={{ fontWeight:600, color:'#185FA5' }}>{m.partner_company || '영업사 미입력'}</div>
                <div style={{ color:'#888', fontSize:12, marginTop:2 }}>
                  {m.manager || '담당자 미입력'}{m.phone ? ' · ' + m.phone : ''}{m.activity_date ? ' · ' + m.activity_date : ''}
                </div>
              </button>
            ))}
          </div>
          <button onClick={() => setCustomerChoice(null)}
            style={{ marginTop:12, width:'100%', padding:'8px', background:'none', border:'none', color:'#888', fontSize:12, cursor:'pointer' }}>
            취소
          </button>
        </div>
      </div>
    )}
    </>
  );
}